<template>
  <section
    id="enhanced-invoice-management-tracker"
    class="invoice-dashboard"
    v-if="$can('read','invoice')"
  >
    <!-- Compact action toolbar (page title lives in navbar PageTitle) -->
    <div class="invoice-dash-header">
      <div class="invoice-dash-header__actions">
        <b-button
          variant="outline-secondary"
          size="sm"
          class="btn-icon"
          aria-label="Refresh"
          @click="loadData"
        >
          <feather-icon icon="RefreshCwIcon" size="15" />
        </b-button>
        <span
          v-b-tooltip.hover
          :title="canDownloadInvoices ? 'Export invoices' : invoiceDownloadDeniedTooltip"
        >
          <b-button
            variant="outline-secondary"
            size="sm"
            class="btn-icon"
            aria-label="Export"
            :disabled="!canDownloadInvoices"
            @click="showExportModal"
          >
            <feather-icon icon="DownloadIcon" size="15" />
          </b-button>
        </span>
        <b-button
          v-if="$can('read','invoice')"
          variant="outline-primary"
          size="sm"
          class="invoice-dash-header__recon"
          :to="{ name: 'apps-invoice-reconciliation' }"
        >
          <feather-icon icon="FileCheckIcon" size="15" class="mr-50" />
          <span class="btn-label">Recon</span>
        </b-button>
        <b-button
          v-if="$can('create','invoice')"
          variant="primary"
          size="sm"
          @click="goToCreateInvoice"
        >
          <feather-icon icon="PlusIcon" size="15" class="mr-50" />
          <span>Create</span>
        </b-button>
      </div>
    </div>

    <!-- Workspace: tabs (Overview filters live inside Overview tab) -->
    <b-card class="invoice-workspace mb-0" body-class="p-0">
      <div class="invoice-workspace__tabs px-1 px-md-2 pb-1">
      <div
        v-if="!hasAnyInvoiceDashboardTab"
        class="invoice-tab-unauthorized text-center py-5 px-2"
      >
        <h5 class="mb-50">You are not authorized</h5>
        <p class="text-muted mb-0">
          You don’t have permission to view any Invoice Tracker tabs.
        </p>
      </div>
      <b-tabs
        v-else
        v-model="activeTab"
        lazy
        nav-class="invoice-dash-tabs"
        content-class="invoice-tab-content"
      >
        <!-- Invoice Overview Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_overview')" title="Invoice Overview">
          <!-- Filters apply only to Invoice Overview (other tabs have their own) -->
          <div class="invoice-filter-toolbar">
            <div class="invoice-filter-toolbar__top">
              <div class="invoice-status-chips">
                <button
                  v-for="chip in statusQuickFiltersWithCounts"
                  :key="chip.value === null ? 'all' : chip.value"
                  type="button"
                  class="invoice-status-chip"
                  :class="{ 'is-active': isStatusChipActive(chip.value) }"
                  @click="setStatusQuickFilter(chip.value)"
                >
                  {{ chip.label }}
                  <span v-if="chip.count != null" class="invoice-status-chip__count">{{ chip.count }}</span>
                </button>
                <button
                  type="button"
                  class="invoice-status-chip invoice-status-chip--danger"
                  :class="{ 'is-active': filters.showOverdueOnly }"
                  @click="setOverdueQuickFilter"
                >
                  Overdue
                  <span v-if="overdueStatusCount != null" class="invoice-status-chip__count">{{ overdueStatusCount }}</span>
                </button>
              </div>

              <div class="invoice-filter-toolbar__actions">
                <b-button
                  v-if="hasActiveFilters"
                  size="sm"
                  variant="outline-secondary"
                  class="invoice-filters-icon-btn"
                  aria-label="Clear filters"
                  @click="clearFilters"
                >
                  <feather-icon icon="XIcon" size="14" />
                  <span class="ml-50 d-none d-md-inline">Clear</span>
                </b-button>
                <b-button
                  size="sm"
                  :variant="overviewPanelFilterCount ? 'primary' : 'outline-primary'"
                  class="invoice-filters-icon-btn"
                  aria-label="Open filters"
                  @click="openOverviewFilters"
                >
                  <feather-icon icon="FilterIcon" size="14" />
                  <span class="ml-50 d-none d-md-inline">Filters</span>
                  <b-badge
                    v-if="overviewPanelFilterCount"
                    variant="light"
                    pill
                    class="ml-50 invoice-filter-count"
                  >
                    {{ overviewPanelFilterCount }}
                  </b-badge>
                </b-button>
              </div>
            </div>
          </div>

          <b-modal
            id="invoice-overview-filters"
            title="Filters"
            size="lg"
            hide-footer
            modal-class="invoice-overview-filters-modal"
            @hidden="filtersExpanded = false"
          >
            <div class="invoice-filter-primary">
              <div class="invoice-filter-field">
                <label>Client</label>
                <b-form-select
                  v-model="filters.client"
                  :options="clientSelectOptions"
                  size="sm"
                  class="invoice-filter-select"
                />
              </div>
              <div class="invoice-filter-field">
                <label>From</label>
                <b-form-input
                  v-model="filters.startDate"
                  type="date"
                  size="sm"
                  placeholder="Start"
                />
              </div>
              <div class="invoice-filter-field">
                <label>To</label>
                <b-form-input
                  v-model="filters.endDate"
                  type="date"
                  size="sm"
                  placeholder="End"
                />
              </div>
              <div class="invoice-filter-field invoice-filter-field--actions">
                <label class="d-none d-md-block">&nbsp;</label>
                <div class="d-flex flex-wrap align-items-center">
                  <b-button variant="primary" size="sm" class="mr-50 mb-50 mb-md-0" @click="applyFilters">
                    Apply
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="mr-50 mb-50 mb-md-0"
                    :disabled="!hasActiveFilters"
                    @click="clearFilters"
                  >
                    Clear
                  </b-button>
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    class="mb-50 mb-md-0"
                    @click="filtersExpanded = !filtersExpanded"
                  >
                    <feather-icon :icon="filtersExpanded ? 'ChevronUpIcon' : 'SlidersIcon'" size="14" class="mr-25" />
                    More
                    <b-badge v-if="advancedFilterCount" variant="primary" class="ml-25 invoice-filter-count">
                      {{ advancedFilterCount }}
                    </b-badge>
                  </b-button>
                </div>
              </div>
            </div>

            <b-collapse :visible="filtersExpanded">
              <div class="invoice-filters-advanced">
                <b-row class="invoice-filters-row">
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Invoice Status" class="mb-0">
                      <b-form-select
                        v-model="filters.status"
                        :options="statusSelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Billing Model" class="mb-0">
                      <b-form-select
                        v-model="filters.billingModel"
                        :options="billingModelSelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Category" class="mb-0">
                      <b-form-select
                        v-model="filters.category"
                        :options="categorySelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Industry (Vertical)" class="mb-0">
                      <b-form-select
                        v-model="filters.industry"
                        :options="industrySelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Billing Month" class="mb-0">
                      <b-form-input
                        v-model="filters.billingMonth"
                        type="month"
                        size="sm"
                        placeholder="YYYY-MM"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="GSTR-1 Status" class="mb-0">
                      <b-form-select
                        v-model="filters.gstr1Status"
                        :options="gstr1StatusSelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Due Bucket" class="mb-0">
                      <b-form-select
                        v-model="filters.dueBucket"
                        :options="dueBucketSelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" lg="3">
                    <b-form-group label="Candidate" class="mb-0">
                      <b-form-select
                        v-model="filters.candidateFilter"
                        :options="candidateFilterSelectOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-collapse>
          </b-modal>

          <invoice-overview-tab
            :items="filteredInvoiceData"
            :fields="invoiceFields"
            :loading="loading"
            :per-page="perPage"
            :can-create="$can('create','invoice')"
            :can-download="canDownloadInvoices"
            @view-invoice="viewInvoiceDetails"
            @view-history="viewInvoiceHistory"
            @download-pdf="downloadInvoicePdf"
            @upload-signed="openSignedInvoiceUpload"
            @download-json="downloadInvoiceOverviewJson"
            @download-excel="downloadInvoiceOverviewExcel"
            @record-payment="recordPayment"
            @update-status="updateStatus"
            @update-gstr="openGstrModal"
            @dispute="openDisputeModal"
            @view-candidate="viewCandidateDetails"
            @update-einvoice="openEInvoiceModal"
            @create-invoice="goToCreateInvoice"
            @duplicate-invoice="duplicateInvoiceForNextMonth"
          />
        </b-tab>

        <!-- Candidate Revenue Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_candidate_revenue')" title="Candidate Revenue">
          <candidate-revenue-tab
            :items="candidateRevenueData"
            :fields="candidateRevenueFields"
            :loading="loading"
            :per-page="perPage"
            :clients="clients"
            :industry-options="industryOptions"
            :candidate-status-options="candidateStatusOptions"
            :category-options="candidateCategoryOptions"
            :initial-filters="candidateRevenueFilters"
            @view-invoice="viewInvoiceDetails"
            @view-candidate="viewCandidateDetails"
            @update-revenue="openUpdateRevenueModal"
            @apply-filters="applyCandidateRevenueFilters"
          />
        </b-tab>

        <!-- Collections & Follow-ups Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_collections')" title="Collections & Follow-ups">
          <collections-tab
            :items="collectionsData"
            :fields="collectionsFields"
            :loading="loading"
            :per-page="perPage"
            :clients="clients"
            :initial-filters="collectionsFilters"
            @view-invoice="viewInvoiceDetails"
            @add-followup="addFollowUp"
            @record-payment="recordPayment"
            @apply-filters="applyCollectionsFilters"
          />
        </b-tab>

        <!-- GSTR-1 Tracking Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_gstr1')" title="GSTR-1 Tracking">
          <gstr1-tab
            :items="gstr1Data"
            :fields="gstr1Fields"
            :loading="loading"
            :clients="clients"
            :initial-filters="gstr1Filters"
            @apply="loadGSTR1Data"
            @export="exportGSTR1"
          />
        </b-tab>

        <!-- GSTR-3B Tracking Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_gstr3b')" title="GSTR-3B Tracking">
          <gstr3b-tab
            :items="gstr3bData"
            :fields="gstr3bFields"
            :loading="loading"
            :clients="clients"
            :summary="gstr3bSummary"
            :initial-filters="gstr3bFilters"
            @apply="loadGSTR3BData"
            @export="exportGSTR3B"
          />
        </b-tab>

        <!-- Finance Dashboard Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_finance_dashboard')" title="Finance Dashboard">
          <finance-dashboard-tab
            :items="financeDashboardData"
            :fields="financeDashboardFields"
            :loading="loading"
            :clients="clients"
            :initial-filters="financeFilters"
            @apply="loadFinanceDashboard"
          />
        </b-tab>

        <!-- Recruiter Dashboard Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_recruiter_dashboard')" title="Recruiter Dashboard">
          <recruiter-dashboard-tab
            :items="recruiterDashboardData"
            :fields="recruiterDashboardFields"
            :loading="loading"
            :per-page="perPage"
            :initial-filters="recruiterFilters"
            @apply="loadRecruiterDashboard"
            @view-recruiter="viewRecruiterDetails"
          />
        </b-tab>

        <!-- Contractual Billing Tab -->
        <b-tab v-if="canViewInvoiceTab('invoice_contractual_billing')" title="Contractual Billing">
          <contractual-billing-tab
            ref="contractualBillingTab"
            :invoices="contractualInvoices"
            :preview-fields="billingSheetPreviewFields"
            :clients="clients"
            :uploading="uploadingSheet"
            :initial-filters="contractualFilters"
            @apply="applyContractualFilters"
            @upload="uploadBillingSheet"
            @file-change="onBillingSheetFileChange"
          />
        </b-tab>
      </b-tabs>
      </div>
    </b-card>

    <!-- Modals (extracted as separate components) -->
    <record-payment-modal
      ref="recordPaymentModal"
      :payment-mode-options="paymentModeOptions"
      @save="savePayment"
    />

    <update-status-modal
      ref="updateStatusModal"
      :status-options="invoiceStatusOptions"
      @save="saveStatusUpdate"
      @open-revise="openReviseEditor"
    />

    <update-gstr-modal
      ref="updateGstrModal"
      @save="saveGstrUpdate"
    />

    <add-follow-up-modal
      ref="addFollowUpModal"
      :follow-up-type-options="followUpTypeOptions"
      @save="saveFollowUp"
    />

    <dispute-modal
      ref="disputeModal"
      @save="saveDispute"
    />

    <update-revenue-modal
      ref="updateRevenueModal"
      :monthly-records="contractualMonthlyRecords"
      :contract-config="contractualConfigData"
      @save-permanent="savePermanentRevenue"
      @save-monthly-data="saveContractualRecord"
      @save-contract-details="saveContractDetails"
    />

    <!-- Invoice View Modal (read-only + signed invoice workflow).
         Clear selection only on @close (after hide) — never null the prop while the modal is still open. -->
    <invoice-view-modal
      :invoice="selectedInvoiceForView"
      @close="selectedInvoiceForView = null"
      @edit="goToUpdateInvoice"
      @duplicate="duplicateInvoiceForNextMonth"
      @signed-uploaded="onSignedInvoiceUploaded"
    />

    <invoice-revision-history-modal
      :invoice="selectedInvoiceForHistory"
      @close="selectedInvoiceForHistory = null"
    />

    <!-- Quick upload signed invoice (from overview actions) -->
    <b-modal
      id="modal-upload-signed-invoice"
      ref="uploadSignedModal"
      title="Upload Signed Invoice"
      size="md"
      hide-footer
      @hidden="signedUploadTarget = null"
    >
      <signed-invoice-workflow
        v-if="signedUploadTarget"
        :invoice="signedUploadTarget"
        :invoice-id="resolveInvoiceApiKey(signedUploadTarget)"
        :can-download="canDownloadInvoices"
        @signed-uploaded="payload => onSignedInvoiceUploaded({ invoice: signedUploadTarget, payload })"
      />
    </b-modal>

    <!-- E-Invoice Status Modal -->
    <e-invoice-status-modal
      ref="eInvoiceStatusModal"
      @save="saveEInvoiceStatus"
    />
  </section>
</template>

<script>
import { BRow, BCol, BCard, BButton, BFormGroup, BFormInput, BFormSelect, BTabs, BTab, BModal, BBadge, BCollapse, VBTooltip } from 'bootstrap-vue'
import moment from 'moment'
import invoiceService from '@/services/invoice-service'
import clientService from '@/services/client-service'
import enhancedInvoiceService from '@/services/enhanced-invoice-service'
import UserService from '@/services/user-service'
import CandidateService from '@/services/candidate-service'
import ContractualService from '@/services/contractual-service'
import ContractualConfigService from '@/services/contractual-config-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import invoiceFormatMixin from '@/mixins/invoiceFormatMixin'
import { buildInvoiceDownloadFilename, buildEInvoiceDownloadFilename } from '@/utils/invoice-filename'
import {
  canDownloadInvoices as checkCanDownloadInvoices,
  canViewInvoiceDashboardTab,
  getAllowedInvoiceDashboardTabKeys,
  resolveInvoiceDashboardTab,
  INVOICE_DOWNLOAD_DENIED_TOOLTIP,
} from '@/utils/invoice-permissions'
import { resolveInvoiceApiKey } from '@/utils/invoice-api-key'
import { buildEInvoiceDocument } from '@/utils/einvoice-json-builder'
import { downloadEInvoiceExcel } from '@/utils/einvoice-excel-builder'
import {
  DEFAULT_INVOICE_STATUS_OPTIONS,
  mergeInvoiceStatusOptions,
  REVISED_STATUS,
  isRevisedStatusValue,
  extractUpdatedInvoiceStatus,
} from '@/utils/invoice-status-options'
import InvoiceViewModal from './components/InvoiceViewModal.vue'
import InvoiceRevisionHistoryModal from './components/modals/InvoiceRevisionHistoryModal.vue'
import { getInvoiceRevisionCount } from '@/utils/invoice-revision-count'
import SignedInvoiceWorkflow from './components/SignedInvoiceWorkflow.vue'
import InvoiceOverviewTab from './components/tabs/InvoiceOverviewTab.vue'
import CandidateRevenueTab from './components/tabs/CandidateRevenueTab.vue'
import CollectionsTab from './components/tabs/CollectionsTab.vue'
import Gstr1Tab from './components/tabs/Gstr1Tab.vue'
import Gstr3bTab from './components/tabs/Gstr3bTab.vue'
import FinanceDashboardTab from './components/tabs/FinanceDashboardTab.vue'
import RecruiterDashboardTab from './components/tabs/RecruiterDashboardTab.vue'
import ContractualBillingTab from './components/tabs/ContractualBillingTab.vue'
import RecordPaymentModal from './components/modals/RecordPaymentModal.vue'
import UpdateStatusModal from './components/modals/UpdateStatusModal.vue'
import UpdateGstrModal from './components/modals/UpdateGstrModal.vue'
import AddFollowUpModal from './components/modals/AddFollowUpModal.vue'
import DisputeModal from './components/modals/DisputeModal.vue'
import UpdateRevenueModal from './components/modals/UpdateRevenueModal.vue'
import EInvoiceStatusModal from './components/modals/EInvoiceStatusModal.vue'

const INVOICE_DASHBOARD_FILTERS_KEY = 'invoiceDashboard_filters_v2'
const INVOICE_DASHBOARD_FILTERS_KEY_LEGACY = 'invoiceDashboard_filters'

const TAB_OVERVIEW = 'overview'
const TAB_CANDIDATE_REVENUE = 'revenue'
const TAB_COLLECTIONS = 'collections'
const TAB_GSTR1 = 'gstr1'
const TAB_GSTR3B = 'gstr3b'
const TAB_FINANCE = 'finance'
const TAB_RECRUITER = 'recruiter'
const TAB_CONTRACTUAL = 'contractual'

function thisMonthDateRange() {
  return {
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  }
}

function defaultOverviewDateRange() {
  return thisMonthDateRange()
}

function isLegacyTwelveMonthDefault(startDate, endDate) {
  if (!startDate || !endDate) return false
  const start = moment(startDate, 'YYYY-MM-DD', true)
  const end = moment(endDate, 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid()) return false
  const span = end.diff(start, 'months', true)
  return span >= 11.4 && span <= 12.6
}

function makeInvoiceWindowKey(startDate, endDate, client, category) {
  return `${startDate || ''}|${endDate || ''}|${client || ''}|${category || ''}`
}

/** Wide scan for Raised / Issued / Part Paid / Overdue chips (ignore Filters month). */
function allMonthsDateRange() {
  return {
    startDate: '2024-01-01',
    endDate: moment().add(1, 'year').format('YYYY-MM-DD')
  }
}

/** Canonical keys from normalizeInvoiceStatusKey that always use the all-months cache. */
const ALL_MONTHS_STATUS_KEYS = ['raised', 'issued', 'partial']

export default {
  name: 'InvoiceDashboard',
  components: {
    InvoiceViewModal,
    InvoiceRevisionHistoryModal,
    SignedInvoiceWorkflow,
    InvoiceOverviewTab,
    CandidateRevenueTab,
    CollectionsTab,
    Gstr1Tab,
    Gstr3bTab,
    FinanceDashboardTab,
    RecruiterDashboardTab,
    ContractualBillingTab,
    RecordPaymentModal,
    UpdateStatusModal,
    UpdateGstrModal,
    AddFollowUpModal,
    DisputeModal,
    UpdateRevenueModal,
    EInvoiceStatusModal,
    BRow, BCol, BCard, BButton, BFormGroup, BFormInput, BFormSelect,
    BTabs, BTab, BModal, BBadge, BCollapse
  },
  directives: { 'b-tooltip': VBTooltip },
  mixins: [invoiceFormatMixin],
  data() {
    return {
      loading: false,
      activeTab: 0,
      activeTabKey: null,
      filtersExpanded: false,
      perPage: 15,
      recruiterIdsCache: null,
      invoicesWindowKey: '',
      invoicesInFlight: null,
      invoicesInFlightKey: '',
      allMonthsInvoiceData: [],
      allMonthsWindowKey: '',
      allMonthsInFlight: null,
      allMonthsInFlightKey: '',
      auxInvoiceCache: {},
      tabLoaded: {
        revenue: false,
        collections: false,
        gstr1: false,
        gstr3b: false,
        finance: false,
        recruiter: false,
        contractual: false
      },
      tabFetchKey: {
        revenue: '',
        collections: '',
        gstr1: '',
        gstr3b: '',
        finance: '',
        recruiter: '',
        contractual: ''
      },
      // Chip filter values are display/filter keys; Part Paid matches API aliases
      // "Part Paid" and "Partially Paid" via normalizeInvoiceStatusKey.
      statusQuickFilters: [
        { label: 'All', value: null },
        { label: 'Draft', value: 'Draft' },
        { label: 'Raised', value: 'Raised' },
        { label: 'Issued', value: 'Issued' },
        { label: 'Part Paid', value: 'Part Paid' },
        { label: 'Paid', value: 'Paid' },
        { label: 'Disputed', value: 'Disputed' }
      ],
      totalRows: 0,
      selectedInvoiceForView: null,
      selectedInvoiceForHistory: null,
      signedUploadTarget: null,

      // Filters (Invoice Overview - default)
      filters: {
        status: null,
        billingModel: null,
        client: null,
        category: null,
        industry: null,
        billingMonth: null,
        gstr1Status: null,
        dueBucket: null,
        startDate: defaultOverviewDateRange().startDate,
        endDate: defaultOverviewDateRange().endDate,
        showOverdueOnly: false,
        candidateFilter: 'all'
      },
      gstr1StatusOptions: [
        { text: 'Not Ready', value: 'Not Ready' },
        { text: 'Ready', value: 'Ready' },
        { text: 'Included', value: 'Included' },
        { text: 'Uploaded', value: 'Uploaded' },
        { text: 'Filed', value: 'Filed' }
      ],
      dueBucketOptions: [
        { text: '0 – 30 days', value: '0-30' },
        { text: '31 – 60 days', value: '31-60' },
        { text: '61 – 90 days', value: '61-90' },
        { text: '90+ days', value: '90+' }
      ],
      candidateFilterOptions: [
        { text: 'All', value: 'all' },
        { text: 'Missing / Not in ATS only', value: 'orphan' }
      ],

      candidateRevenueFilters: {
        startDate: thisMonthDateRange().startDate,
        endDate: thisMonthDateRange().endDate,
        client: null,
        industry: null,
        candidateStatus: '',
        category: ''
      },
      candidateStatusOptions: [
        { text: 'Joined', value: 'Joined' },
        { text: 'Suspense', value: 'Suspense' },
        { text: 'All Statuses', value: '' }
      ],
      candidateCategoryOptions: [
        { text: 'White Collar', value: 'White Collar' },
        { text: 'Blue Collar', value: 'Blue Collar' },
        { text: 'All (White & Blue Collar)', value: '' }
      ],
      collectionsFilters: {
        startDate: thisMonthDateRange().startDate,
        endDate: thisMonthDateRange().endDate,
        client: null
      },
      contractualFilters: {
        client: null
      },

      // Data arrays
      invoiceData: [],
      candidateRevenueData: [],
      collectionsData: [],
      gstr1Data: [],
      gstr3bData: [],
      gstr3bSummary: null,

      // Options — mergeInvoiceStatusOptions ensures Revised even when API statusList omits it
      invoiceStatusOptions: [...DEFAULT_INVOICE_STATUS_OPTIONS],
      billingModelOptions: [
        { text: 'Permanent Hiring', value: 'Permanent Hiring' },
        { text: 'Contractual Staffing', value: 'Contractual Staffing' },
        { text: 'Cost Plus', value: 'Cost Plus' },
        { text: 'Payroll', value: 'Payroll' }
      ],
      categoryOptions: [
        { text: 'Recruitment', value: 'Recruitment' },
        { text: 'Training', value: 'Training' },
        { text: 'Anti Fraud', value: 'Anti Fraud' },
        { text: 'Contractual', value: 'Contractual' },
        { text: 'Payroll', value: 'Payroll' },
        { text: 'BGV', value: 'BGV' },
        { text: 'Miscellaneous', value: 'Miscellaneous' }
      ],
      industryOptions: [
        { text: 'Service Industry', value: 'Service Industry' },
        { text: 'Infra Industry', value: 'Infra Industry' }
      ],
      clients: [],
      paymentModeOptions: [
        { value: 'Cash', text: 'Cash' },
        { value: 'Cheque', text: 'Cheque' },
        { value: 'NEFT', text: 'NEFT' },
        { value: 'RTGS', text: 'RTGS' },
        { value: 'UPI', text: 'UPI' },
        { value: 'Bank Transfer', text: 'Bank Transfer' }
      ],
      followUpTypeOptions: [
        { value: 'Friendly Reminder', text: 'Friendly Reminder' },
        { value: 'Payment Reminder', text: 'Payment Reminder' },
        { value: 'Escalation', text: 'Escalation' },
        { value: 'Finance Escalation', text: 'Finance Escalation' }
      ],

      // GSTR Filters
      gstr1Filters: {
        period: moment().format('YYYY-MM'),
        client: null
      },
      gstr3bFilters: {
        period: moment().format('YYYY-MM'),
        client: null
      },

      // Table fields - stacked layout: Invoice#+Date+Category, Client+Candidate
      invoiceFields: [
        { key: 'invoice_info', label: 'Invoice', sortable: true, tdClass: 'text-nowrap' },
        {
          key: 'client_candidate',
          label: 'Client / Candidate',
          sortable: true,
          class: 'inv-col-client',
          tdClass: 'text-wrap text-break'
        },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'base_amount', label: 'Base Amount', sortable: true, class: 'inv-col-secondary' },
        { key: 'amount_receivable', label: 'Amount Receivable', sortable: true, class: 'inv-col-tertiary' },
        { key: 'outstanding_amount', label: 'Outstanding', sortable: true },
        { key: 'due_date', label: 'Due Date', sortable: true },
        { key: 'gstr1_status', label: 'GSTR-1', sortable: true, class: 'inv-col-secondary' },
        { key: 'einvoice_status', label: 'E-Invoice', sortable: false, thStyle: { minWidth: '100px' }, class: 'inv-col-secondary' },
        { key: 'actions', label: '', thStyle: { width: '120px' }, tdClass: 'text-nowrap text-right' }
      ],
      candidateRevenueFields: [
        { key: 'candidate_name', label: 'Candidate', sortable: true },
        { key: 'client_name', label: 'Client', sortable: true },
        { key: 'invoice_number', label: 'Invoice #', sortable: true, class: 'inv-col-tertiary' },
        { key: 'revenue_amount', label: 'Revenue Amount', sortable: true },
        { key: 'billing_model', label: 'Billing Model', sortable: true, class: 'inv-col-secondary' },
        { key: 'billing_period', label: 'Billing Period', sortable: true, class: 'inv-col-secondary' },
        { key: 'actions', label: 'Actions', thStyle: { width: '80px' } }
      ],
      collectionsFields: [
        { key: 'invoice_info', label: 'Invoice', sortable: true },
        { key: 'client_gstin', label: 'Client', sortable: true },
        { key: 'due_date', label: 'Due Date', sortable: true },
        { key: 'days_overdue', label: 'Days Overdue', sortable: true, class: 'inv-col-tertiary' },
        { key: 'outstanding_amount', label: 'Outstanding', sortable: true },
        { key: 'follow_up_status', label: 'Follow-up Status', sortable: true, class: 'inv-col-secondary' },
        { key: 'last_follow_up_date', label: 'Last Follow-up', sortable: true, class: 'inv-col-secondary' },
        { key: 'actions', label: 'Actions' }
      ],
      gstr1Fields: [
        { key: 'invoice_info', label: 'Invoice', sortable: true },
        { key: 'client_gstin', label: 'Client', sortable: true },
        { key: 'taxable_value', label: 'Taxable Value', sortable: true },
        { key: 'cgst', label: 'CGST', sortable: true, class: 'inv-col-secondary' },
        { key: 'sgst', label: 'SGST', sortable: true, class: 'inv-col-secondary' },
        { key: 'igst', label: 'IGST', sortable: true, class: 'inv-col-tertiary' },
        { key: 'total_gst', label: 'Total GST', sortable: true }
      ],
      gstr3bFields: [
        { key: 'invoice_info', label: 'Invoice', sortable: true },
        { key: 'client_gstin', label: 'Client', sortable: true },
        { key: 'taxable_value', label: 'Taxable Value', sortable: true },
        { key: 'cgst', label: 'CGST', sortable: true, class: 'inv-col-secondary' },
        { key: 'sgst', label: 'SGST', sortable: true, class: 'inv-col-secondary' },
        { key: 'igst', label: 'IGST', sortable: true, class: 'inv-col-tertiary' }
      ],
      recruiterDashboardFields: [
        { key: 'recruiter_name', label: 'Recruiter', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'placements_billed', label: 'Placements Billed', sortable: true },
        { key: 'clients_count', label: 'Clients', sortable: true, class: 'inv-col-secondary' },
        { key: 'avg_revenue_per_placement', label: 'Avg Revenue/Placement', sortable: true, class: 'inv-col-tertiary' }
      ],
      billingSheetPreviewFields: [
        { key: 'candidate', label: 'Candidate' },
        { key: 'days', label: 'Days' },
        { key: 'rate', label: 'Rate/Day' },
        { key: 'amount', label: 'Amount' }
      ],

      // Finance Dashboard
      financeDashboardData: [],
      financeDashboardFields: [
        { key: 'invoice_info', label: 'Invoice', sortable: true },
        { key: 'client_gstin', label: 'Client', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'due_date', label: 'Due Date', sortable: true, class: 'inv-col-tertiary' },
        { key: 'total_amount', label: 'Total', sortable: true },
        { key: 'paid_amount', label: 'Paid', sortable: true, class: 'inv-col-secondary' },
        { key: 'outstanding', label: 'Outstanding', sortable: true }
      ],
      financeFilters: {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        client: null
      },

      // Recruiter Dashboard
      recruiterDashboardData: [],
      recruiters: [],
      recruiterFilters: {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        recruiterId: null
      },

      // Contractual Billing Sheet
      billingSheetForm: {
        invoice_id: null,
        file: null
      },
      billingSheetPreview: [],
      contractualInvoices: [],
      uploadingSheet: false,

      // Update Revenue Modal
      selectedRevenueCandidate: null,
      contractualMonthlyRecords: [],
      contractualConfigData: null,
      contractualForm: {
        month: '',
        revenue: null,
        start_date: '',
        end_date: '',
        invoice_id: '',
        editId: null
      },
      permanentRevenueForm: {
        revenue: null,
        revenue_confirmed: null
      },
      revenueConfirmedOptions: [
        { title: 'Yes', value: 'Yes' },
        { title: 'No', value: 'No' }
      ]
    }
  },
  computed: {
    canDownloadInvoices() {
      return checkCanDownloadInvoices(this.$can.bind(this))
    },
    allowedInvoiceTabKeys() {
      return getAllowedInvoiceDashboardTabKeys(this.$can.bind(this))
    },
    hasAnyInvoiceDashboardTab() {
      return this.allowedInvoiceTabKeys.length > 0
    },
    invoiceDownloadDeniedTooltip() {
      return INVOICE_DOWNLOAD_DENIED_TOOLTIP
    },
    clientSelectOptions() {
      return [
        { value: null, text: 'All clients' },
        ...this.clients.map(c => ({
          value: c.title != null ? c.title : (c.text != null ? c.text : c.value),
          text: c.title != null ? c.title : (c.text != null ? c.text : String(c.value ?? ''))
        }))
      ]
    },
    statusSelectOptions() {
      return [
        { value: null, text: 'Select Status' },
        ...this.invoiceStatusOptions.map(o => ({
          value: o.value,
          text: o.text || o.title || o.label || String(o.value ?? '')
        }))
      ]
    },
    billingModelSelectOptions() {
      return [
        { value: null, text: 'Select Billing Model' },
        ...this.billingModelOptions.map(o => ({
          value: o.value,
          text: o.text || o.label || String(o.value ?? '')
        }))
      ]
    },
    categorySelectOptions() {
      return [
        { value: null, text: 'Select Category' },
        ...this.categoryOptions.map(o => ({
          value: o.title != null ? o.title : (o.text != null ? o.text : o.value),
          text: o.text || o.title || String(o.value ?? '')
        }))
      ]
    },
    industrySelectOptions() {
      return [
        { value: null, text: 'Select Industry' },
        ...this.industryOptions.map(o => ({
          value: o.title != null ? o.title : (o.text != null ? o.text : o.value),
          text: o.text || o.title || String(o.value ?? '')
        }))
      ]
    },
    gstr1StatusSelectOptions() {
      return [
        { value: null, text: 'All' },
        ...this.gstr1StatusOptions
      ]
    },
    dueBucketSelectOptions() {
      return [
        { value: null, text: 'All' },
        ...this.dueBucketOptions
      ]
    },
    candidateFilterSelectOptions() {
      return this.candidateFilterOptions
    },
    invoiceStatusCounts() {
      const counts = { __all: 0, __overdue: 0 }
      const windowList = Array.isArray(this.invoiceData) ? this.invoiceData : []
      const allMonthsReady = this.allMonthsWindowKey && Array.isArray(this.allMonthsInvoiceData)
      const allMonthsList = allMonthsReady ? this.allMonthsInvoiceData : windowList
      counts.__all = windowList.length
      windowList.forEach(inv => {
        const key = this.normalizeInvoiceStatusKey(inv.status)
        if (key && !ALL_MONTHS_STATUS_KEYS.includes(key)) {
          counts[key] = (counts[key] || 0) + 1
        }
      })
      allMonthsList.forEach(inv => {
        const key = this.normalizeInvoiceStatusKey(inv.status)
        if (key && ALL_MONTHS_STATUS_KEYS.includes(key)) {
          counts[key] = (counts[key] || 0) + 1
        }
        if (this.isOverdueInvoice(inv)) counts.__overdue += 1
      })
      return counts
    },
    statusQuickFiltersWithCounts() {
      return this.statusQuickFilters.map(chip => {
        if (chip.value == null) {
          return { ...chip, count: this.invoiceStatusCounts.__all }
        }
        const key = this.normalizeInvoiceStatusKey(chip.value)
        const count = this.invoiceStatusCounts[key]
        return { ...chip, count: count != null ? count : 0 }
      })
    },
    overdueStatusCount() {
      return this.invoiceStatusCounts.__overdue
    },
    advancedFilterCount() {
      const f = this.filters
      let n = 0
      if (f.billingModel) n += 1
      if (f.category) n += 1
      if (f.industry) n += 1
      if (f.billingMonth) n += 1
      if (f.gstr1Status) n += 1
      if (f.dueBucket) n += 1
      if (f.candidateFilter && f.candidateFilter !== 'all') n += 1
      if (
        f.status &&
        !this.statusQuickFilters.some(c => c.value != null && this.invoiceStatusesMatch(c.value, f.status))
      ) n += 1
      return n
    },
    isDefaultOverviewDateRange() {
      const d = defaultOverviewDateRange()
      return this.filters.startDate === d.startDate && this.filters.endDate === d.endDate
    },
    // Badge for filter icon: client / dates / More panel fields (not status chips)
    overviewPanelFilterCount() {
      const f = this.filters
      let n = 0
      if (f.client) n += 1
      if (f.startDate && !this.isDefaultOverviewDateRange) n += 1
      if (f.endDate && !this.isDefaultOverviewDateRange) n += 1
      if (f.billingModel) n += 1
      if (f.category) n += 1
      if (f.industry) n += 1
      if (f.billingMonth) n += 1
      if (f.gstr1Status) n += 1
      if (f.dueBucket) n += 1
      if (f.candidateFilter && f.candidateFilter !== 'all') n += 1
      if (
        f.status &&
        !this.statusQuickFilters.some(c => c.value != null && this.invoiceStatusesMatch(c.value, f.status))
      ) n += 1
      return n
    },
    hasActiveFilters() {
      const f = this.filters
      const customDates = !!(f.startDate || f.endDate) && !this.isDefaultOverviewDateRange
      return !!(
        f.status || f.billingModel || f.client || f.category || f.industry ||
        f.billingMonth || f.gstr1Status || f.dueBucket || customDates ||
        f.showOverdueOnly || (f.candidateFilter && f.candidateFilter !== 'all')
      )
    },
    isContractualRevenue() {
      return this.isContractualBillingModel(this.selectedRevenueCandidate?.billing_model)
    },
    filteredInvoiceData() {
      const useAllMonths = this.isAllMonthsQuickFilterActive()
      const allMonthsReady = this.allMonthsWindowKey && Array.isArray(this.allMonthsInvoiceData)
      let data = (useAllMonths && allMonthsReady) ? this.allMonthsInvoiceData : this.invoiceData

      // Status filter (client-side so Part Paid / Partially Paid aliases both match)
      if (this.filters.status && !this.filters.showOverdueOnly) {
        const wanted = this.normalizeInvoiceStatusKey(this.filters.status)
        data = data.filter(inv => this.normalizeInvoiceStatusKey(inv.status) === wanted)
      }

      // Overdue quick-filter chip — all-months + outstanding > 0
      if (this.filters.showOverdueOnly) {
        data = data.filter(inv => this.isOverdueInvoice(inv))
      }

      // Candidate filter
      if (this.filters.candidateFilter === 'orphan') {
        data = data.filter(inv => {
          const hasId = !!(inv.candidate_id || inv.candidateId)
          const hasName = !!(inv.candidate_name || inv.candidateName || '').trim()
          return hasId && !hasName
        })
      }

      if (this.filters.billingModel) {
        data = data.filter(inv => (inv.billing_model || '') === this.filters.billingModel)
      }

      if (this.filters.industry) {
        const filterIndustry = (this.filters.industry || '').trim().toLowerCase()
        data = data.filter(inv => (inv.vertical || '').trim().toLowerCase() === filterIndustry)
      }

      // Billing Month (YYYY-MM)
      if (this.filters.billingMonth) {
        data = data.filter(inv => {
          const bm = (inv.billing_month || inv.invoice_date || '').substring(0, 7)
          return bm === this.filters.billingMonth
        })
      }

      // GSTR-1 Status
      if (this.filters.gstr1Status) {
        data = data.filter(inv => (inv.gstr1_status || 'Not Ready') === this.filters.gstr1Status)
      }

      // Due Bucket
      if (this.filters.dueBucket) {
        data = data.filter(inv => {
          if (!inv.due_date) return false
          const today = new Date()
          const due = new Date(inv.due_date)
          const diff = Math.ceil((today - due) / (1000 * 60 * 60 * 24))
          if (this.filters.dueBucket === '0-30') return diff >= 0 && diff <= 30
          if (this.filters.dueBucket === '31-60') return diff >= 31 && diff <= 60
          if (this.filters.dueBucket === '61-90') return diff >= 61 && diff <= 90
          if (this.filters.dueBucket === '90+') return diff > 90
          return true
        })
      }

      return data
    }
  },
  created() {
    const restored = this.restoreFiltersFromSession()
    if (!restored && !this.filters.startDate && !this.filters.endDate) {
      const range = defaultOverviewDateRange()
      this.filters.startDate = range.startDate
      this.filters.endDate = range.endDate
    }
    this.syncActiveTabFromPermissions()
  },
  watch: {
    activeTab(index) {
      const key = this.allowedInvoiceTabKeys[index]
      if (key && key !== this.activeTabKey) {
        this.activeTabKey = key
      }
      if (this.activeTabKey) {
        this.ensureTabData(this.activeTabKey)
      }
    },
    '$route.query.tab'(tab) {
      const key = resolveInvoiceDashboardTab(this.$can.bind(this), tab)
      if (!key || key === this.activeTabKey) return
      if (!this.allowedInvoiceTabKeys.includes(key)) return
      this.activeTabKey = key
      const idx = this.allowedInvoiceTabKeys.indexOf(key)
      if (idx >= 0) this.activeTab = idx
    }
  },
  async mounted() {
    this.loading = true
    try {
      await Promise.all([
        this.loadClients(),
        this.loadInvoices(),
        this.loadAllMonthsInvoices()
      ])
      if (this.activeTabKey && this.activeTabKey !== TAB_OVERVIEW) {
        await this.ensureTabData(this.activeTabKey)
      }
    } catch (error) {
      console.error('[InvoiceDashboard] Error loading data:', error)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          text: 'Failed to load data. Please try again.',
          icon: 'AlertCircleIcon',
          variant: 'danger'
        }
      })
    } finally {
      this.loading = false
    }
  },
  methods: {
    resolveInvoiceApiKey,
    canViewInvoiceTab(subject) {
      return canViewInvoiceDashboardTab(this.$can.bind(this), subject)
    },
    syncActiveTabFromPermissions() {
      const allowed = this.allowedInvoiceTabKeys
      const requested = this.$route && this.$route.query ? this.$route.query.tab : ''
      const key = resolveInvoiceDashboardTab(this.$can.bind(this), requested)
      this.activeTabKey = key
      const idx = key ? allowed.indexOf(key) : -1
      this.activeTab = idx >= 0 ? idx : 0
    },
    persistFiltersToSession() {
      try {
        sessionStorage.setItem(INVOICE_DASHBOARD_FILTERS_KEY, JSON.stringify(this.filters))
      } catch (e) { /* noop */ }
    },
    restoreFiltersFromSession() {
      try {
        const raw = sessionStorage.getItem(INVOICE_DASHBOARD_FILTERS_KEY)
          || sessionStorage.getItem(INVOICE_DASHBOARD_FILTERS_KEY_LEGACY)
        if (!raw) return false
        const saved = JSON.parse(raw)
        if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
          this.filters = { ...this.filters, ...saved }
          if (isLegacyTwelveMonthDefault(this.filters.startDate, this.filters.endDate)) {
            const range = defaultOverviewDateRange()
            this.filters.startDate = range.startDate
            this.filters.endDate = range.endDate
            this.persistFiltersToSession()
          }
          return true
        }
      } catch (e) { /* noop */ }
      return false
    },
    clearPersistedFilters() {
      try {
        sessionStorage.removeItem(INVOICE_DASHBOARD_FILTERS_KEY)
        sessionStorage.removeItem(INVOICE_DASHBOARD_FILTERS_KEY_LEGACY)
      } catch (e) { /* noop */ }
    },

    overviewWindowKey() {
      return makeInvoiceWindowKey(
        this.filters.startDate || '',
        this.filters.endDate || '',
        this.filters.client || '',
        this.filters.category || ''
      )
    },

    allMonthsScopeKey() {
      return `allMonths|${this.filters.client || ''}|${this.filters.category || ''}`
    },

    isAllMonthsStatusChip(status) {
      const key = this.normalizeInvoiceStatusKey(status)
      return !!key && ALL_MONTHS_STATUS_KEYS.includes(key)
    },

    isAllMonthsQuickFilterActive() {
      return !!(this.filters.showOverdueOnly || this.isAllMonthsStatusChip(this.filters.status))
    },

    invoiceOutstanding(inv) {
      if (!inv) return 0
      if (inv.outstanding_amount != null && inv.outstanding_amount !== '') {
        const n = parseFloat(inv.outstanding_amount)
        if (!Number.isNaN(n)) return n
      }
      const total = parseFloat(inv.total_amount) || parseFloat(inv.amount_with_gst) || 0
      const paid = parseFloat(inv.paid_amount) || 0
      return Math.max(0, total - paid)
    },

    isOverdueInvoice(inv) {
      if (!inv) return false
      if (!this.isOverdue(inv.due_date || inv.dueDate)) return false
      return this.invoiceOutstanding(inv) > 0
    },

    invoiceCacheMatch(inv, invoiceKey) {
      if (!inv || invoiceKey == null || invoiceKey === '') return false
      return (inv.invoice_id || inv.id) === invoiceKey || inv.id === invoiceKey || inv.invoice_id === invoiceKey
    },

    findCachedInvoice(invoiceKey) {
      const fromAll = (this.allMonthsInvoiceData || []).find(i => this.invoiceCacheMatch(i, invoiceKey))
      if (fromAll) return fromAll
      return (this.invoiceData || []).find(i => this.invoiceCacheMatch(i, invoiceKey)) || null
    },

    patchInvoiceCaches(invoiceKey, patch) {
      const seen = new Set()
      ;[this.invoiceData, this.allMonthsInvoiceData].forEach((list) => {
        const inv = (list || []).find(i => this.invoiceCacheMatch(i, invoiceKey))
        if (inv && !seen.has(inv)) {
          seen.add(inv)
          patch(inv)
        }
      })
    },

    mapInvoiceRow(invoice) {
      const totalAmount = parseFloat(invoice.total_amount) || parseFloat(invoice.amount_with_gst) || 0
      const paidAmount = parseFloat(invoice.paid_amount) || 0
      let candidateId = invoice.candidate_id || invoice.candidateId
      let candidateName = invoice.candidate_name || invoice.candidateName
      let jobId = invoice.job_id
      if ((!candidateId && !candidateName) && invoice.candidateList && Array.isArray(invoice.candidateList) && invoice.candidateList.length > 0) {
        const first = invoice.candidateList[0]
        candidateId = first.cid || first.CAN_ID || first.candidate_id
        candidateName = first.CANDIDATE_NAME || first.candidate_name
        jobId = first.jid || first.JOB_ID || jobId
      }
      const candidateDisplay = this.getCandidateDisplay({ ...invoice, candidate_id: candidateId, candidate_name: candidateName })
      return {
        ...invoice,
        candidate_id: candidateId,
        candidate_name: candidateName,
        candidate_display: candidateDisplay.display,
        candidate_linkable: candidateDisplay.linkable,
        invoice_info: `${invoice.invoice_id || ''} ${invoice.invoice_date || ''}`,
        client_candidate: `${invoice.client || ''} ${candidateName || candidateDisplay.display || ''}`,
        category_billing: `${invoice.category || ''} ${invoice.billing_model || ''}`,
        id: invoice.id || invoice.invoice_id,
        invoice_number: invoice.invoice_id || invoice.invoice_number || 'N/A',
        client_name: invoice.client || invoice.client_name || 'N/A',
        total_amount: totalAmount,
        paid_amount: paidAmount,
        outstanding_amount: Math.max(0, totalAmount - paidAmount),
        billing_model: invoice.billing_model || this.inferBillingModelFromCategory(invoice.category),
        due_date: invoice.due_date || invoice.dueDate,
        r1_filed: invoice.r1_filed || null,
        gstr1_status: invoice.gstr1_status
          || (invoice.r1_filed === 'Yes' || invoice.gstr1_filed ? 'Filed'
            : (invoice.r1_filed === 'No' ? 'Not Ready' : 'Not Ready')),
        gstr1_period: invoice.gstr1_period || invoice.billing_month || null,
        einvoice_filed: this.normalizeEinvoiceFiled(invoice.einvoice_filed),
        job_id: jobId,
        last_follow_up_date: invoice.last_follow_up_date || invoice.lastFollowUpDate || null,
        follow_up_type: invoice.follow_up_type || invoice.followUpType || null,
        revision_count: getInvoiceRevisionCount(invoice),
      }
    },

    currentTabFetchKey(tabKey) {
      if (tabKey === 'revenue') {
        const f = this.candidateRevenueFilters
        return `rev|${f.startDate || ''}|${f.endDate || ''}|${f.client || ''}|${f.industry || ''}|${f.candidateStatus || ''}|${f.category || ''}`
      }
      if (tabKey === 'collections') {
        const f = this.collectionsFilters
        return `col|${f.startDate || ''}|${f.endDate || ''}|${f.client || ''}`
      }
      if (tabKey === 'gstr1') {
        const f = this.gstr1Filters
        return `g1|${f.period || ''}|${f.client || ''}`
      }
      if (tabKey === 'gstr3b') {
        const f = this.gstr3bFilters
        return `g3|${f.period || ''}|${f.client || ''}`
      }
      if (tabKey === 'finance') {
        const f = this.financeFilters
        return `fin|${f.startDate || ''}|${f.endDate || ''}|${f.client || ''}`
      }
      if (tabKey === 'recruiter') {
        const f = this.recruiterFilters
        return `rec|${f.startDate || ''}|${f.endDate || ''}|${f.recruiterId || ''}`
      }
      if (tabKey === 'contractual') {
        return `con|${this.contractualFilters.client || ''}`
      }
      return tabKey
    },

    markTabLoaded(tabKey) {
      this.tabLoaded[tabKey] = true
      this.tabFetchKey[tabKey] = this.currentTabFetchKey(tabKey)
    },

    invalidateInvoiceDerivedTabs() {
      ;['collections', 'gstr1', 'gstr3b', 'finance'].forEach((key) => {
        this.tabLoaded[key] = false
        this.tabFetchKey[key] = ''
      })
      this.auxInvoiceCache = {}
    },

    parseInvoiceList(res) {
      if (res && res.status === 'OK') {
        if (Array.isArray(res.data && res.data.data)) return res.data.data
        if (Array.isArray(res.data)) return res.data
      }
      return []
    },

    canReuseOverviewInvoices(startDate, endDate, client = '', category = '') {
      if (!this.invoicesWindowKey || !Array.isArray(this.invoiceData)) return false
      const [loadStart, loadEnd, loadClient, loadCategory] = this.invoicesWindowKey.split('|')
      if (loadCategory && category && loadCategory !== category) return false
      if (loadCategory && !category) return false
      if (loadClient && client && loadClient !== client) return false
      if (loadClient && !client) return false
      if (!loadStart && !loadEnd) return true
      if (!startDate && !endDate) return false
      const reqStart = startDate || '0000-01-01'
      const reqEnd = endDate || '9999-12-31'
      const coveredStart = loadStart || '0000-01-01'
      const coveredEnd = loadEnd || '9999-12-31'
      return reqStart >= coveredStart && reqEnd <= coveredEnd
    },

    filterInvoicesForWindow(invoices, startDate, endDate, client = '') {
      if (!Array.isArray(invoices)) return []
      return invoices.filter((inv) => {
        if (client) {
          const name = inv.client || inv.client_name || ''
          if (name !== client) return false
        }
        const d = String(inv.invoice_date || '').substring(0, 10)
        if (startDate && d && d < startDate) return false
        if (endDate && d && d > endDate) return false
        return true
      })
    },

    async getInvoicesForWindow(startDate, endDate, client = '', category = '') {
      if (this.canReuseOverviewInvoices(startDate, endDate, client, category)) {
        return this.filterInvoicesForWindow(this.invoiceData, startDate, endDate, client)
      }
      const key = makeInvoiceWindowKey(startDate, endDate, client, category)
      if (this.auxInvoiceCache[key]) return this.auxInvoiceCache[key]
      const res = await invoiceService.getAll(
        startDate || '',
        endDate || '',
        '',
        client || '',
        category || '',
        'invoice_date'
      )
      const list = this.parseInvoiceList(res)
      this.$set(this.auxInvoiceCache, key, list)
      return list
    },

    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadInvoices(),
          this.loadAllMonthsInvoices({ force: true })
        ])
        await this.refreshVisitedTabData()
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading data:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load data. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.loading = false
      }
    },

    refreshAfterInvoiceChange() {
      const cf = this.collectionsFilters
      if ((this.tabLoaded.collections || this.tabFetchKey.collections)
        && this.canReuseOverviewInvoices(cf.startDate || '', cf.endDate || '', cf.client || '')) {
        this.collectionsData = this.buildCollectionsFromInvoices(
          this.filterInvoicesForWindow(this.invoiceData, cf.startDate || '', cf.endDate || '', cf.client || '')
        )
      }
      if (this.tabFetchKey.finance && this.canReuseOverviewInvoices(
        this.financeFilters.startDate || '',
        this.financeFilters.endDate || '',
        this.financeFilters.client || ''
      )) {
        this.financeDashboardData = this.mapFinanceRows(
          this.filterInvoicesForWindow(
            this.invoiceData,
            this.financeFilters.startDate || '',
            this.financeFilters.endDate || '',
            this.financeFilters.client || ''
          )
        )
      }
    },

    tabLoaderFor(tabIndex) {
      const loaders = {
        [TAB_CANDIDATE_REVENUE]: { key: 'revenue', fn: () => this.loadCandidateRevenue() },
        [TAB_COLLECTIONS]: { key: 'collections', fn: () => this.loadCollectionsData() },
        [TAB_GSTR1]: { key: 'gstr1', fn: () => this.loadGSTR1Data(null, false) },
        [TAB_GSTR3B]: { key: 'gstr3b', fn: () => this.loadGSTR3BData(null, false) },
        [TAB_FINANCE]: { key: 'finance', fn: () => this.loadFinanceDashboard(null, false) },
        [TAB_RECRUITER]: { key: 'recruiter', fn: () => this.loadRecruiterDashboard(null, false) },
        [TAB_CONTRACTUAL]: { key: 'contractual', fn: () => this.loadContractualInvoices() }
      }
      return loaders[tabIndex] || null
    },

    async refreshVisitedTabData() {
      if (this.activeTabKey === TAB_OVERVIEW) {
        if (this.tabLoaded.collections) {
          const cf = this.collectionsFilters
          if (this.canReuseOverviewInvoices(cf.startDate || '', cf.endDate || '', cf.client || '')) {
            this.collectionsData = this.buildCollectionsFromInvoices(
              this.filterInvoicesForWindow(this.invoiceData, cf.startDate || '', cf.endDate || '', cf.client || '')
            )
            this.markTabLoaded('collections')
          } else {
            this.tabLoaded.collections = false
            this.tabFetchKey.collections = ''
          }
        }
        return
      }
      const loader = this.tabLoaderFor(this.activeTabKey)
      if (!loader) return
      this.tabFetchKey[loader.key] = ''
      await loader.fn()
      this.markTabLoaded(loader.key)
    },

    async ensureTabData(tabKey) {
      if (!tabKey || tabKey === TAB_OVERVIEW) return
      if (!this.allowedInvoiceTabKeys.includes(tabKey)) return
      const loader = this.tabLoaderFor(tabKey)
      if (!loader) return
      const key = this.currentTabFetchKey(loader.key)
      if (this.tabFetchKey[loader.key] === key) return

      this.loading = true
      try {
        await loader.fn()
        this.markTabLoaded(loader.key)
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading tab data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadInvoices() {
      const startDate = this.filters.startDate || ''
      const endDate = this.filters.endDate || ''
      // Status / overdue filtered client-side (alias-safe + chip counts from full list)
      const status = ''
      const client = this.filters.client || ''
      const category = this.filters.category || ''
      const dateType = 'invoice_date' // Use same dateType as InvoiceTracker
      const windowKey = makeInvoiceWindowKey(startDate, endDate, client, category)

      if (this.invoicesInFlight && this.invoicesInFlightKey === windowKey) {
        return this.invoicesInFlight
      }

      const run = async () => {
      try {
        const res = await invoiceService.getAll(startDate, endDate, status, client, category, dateType)

        if (res && res.status === 'OK') {
          // Use same response structure as InvoiceTracker: res.data.data
          const invoices = Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
          
          // API statusList may omit Revised — always merge it in for update-status dropdown
          if (res.data.statusList && Array.isArray(res.data.statusList)) {
            this.invoiceStatusOptions = mergeInvoiceStatusOptions(res.data.statusList)
          }

          this.invoiceData = invoices.map(invoice => this.mapInvoiceRow(invoice))

          this.totalRows = this.invoiceData.length
          this.invoicesWindowKey = windowKey
        } else if (res && (res.status === 401 || res.status === 403)) {
          console.warn('[InvoiceDashboard] Session expired (401/403), redirecting to login')
          // Handle session expiry same as InvoiceTracker
          window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
          localStorage.clear()
          this.$router.push({ name: 'auth-login' })
          this.invoiceData = []
          this.totalRows = 0
        } else if (res && res.status === 400) {
          if (!this.isAllMonthsQuickFilterActive()) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'No Data Found',
                text: 'No Data Found for Selected Dates',
                icon: 'InfoIcon',
                variant: 'info'
              }
            })
          }
          this.invoiceData = []
          this.totalRows = 0
        } else if (res && res.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: res.data?.message || 'Server error. Kindly contact IT admin.',
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
          this.invoiceData = []
          this.totalRows = 0
        } else {
          this.invoiceData = []
          this.totalRows = 0
        }
      } catch (error) {
        console.error('Error loading invoices:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load invoices. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        this.invoiceData = []
        this.totalRows = 0
      }
      }

      this.invoicesInFlightKey = windowKey
      this.invoicesInFlight = run().finally(() => {
        if (this.invoicesInFlightKey === windowKey) {
          this.invoicesInFlight = null
          this.invoicesInFlightKey = ''
        }
      })
      return this.invoicesInFlight
    },

    async loadAllMonthsInvoices({ force = false } = {}) {
      const range = allMonthsDateRange()
      const client = this.filters.client || ''
      const category = this.filters.category || ''
      const scopeKey = this.allMonthsScopeKey()

      if (!force && this.allMonthsWindowKey === scopeKey && Array.isArray(this.allMonthsInvoiceData)) {
        return this.allMonthsInvoiceData
      }
      if (this.allMonthsInFlight && this.allMonthsInFlightKey === scopeKey && !force) {
        return this.allMonthsInFlight
      }

      const run = async () => {
        try {
          const res = await invoiceService.getAll(
            range.startDate,
            range.endDate,
            '',
            client,
            category,
            'invoice_date'
          )
          if (res && res.status === 'OK') {
            const invoices = Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
            if (res.data.statusList && Array.isArray(res.data.statusList)) {
              this.invoiceStatusOptions = mergeInvoiceStatusOptions(res.data.statusList)
            }
            this.allMonthsInvoiceData = invoices.map(invoice => this.mapInvoiceRow(invoice))
            this.allMonthsWindowKey = scopeKey
          } else if (res && (res.status === 401 || res.status === 403)) {
            console.warn('[InvoiceDashboard] Session expired (401/403) on all-months invoice scan')
            window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
            localStorage.clear()
            this.$router.push({ name: 'auth-login' })
            this.allMonthsInvoiceData = []
            this.allMonthsWindowKey = ''
          } else if (res && res.status === 500) {
            console.error('[InvoiceDashboard] All-months invoice scan failed', res.data && res.data.message)
            if (!this.allMonthsWindowKey) this.allMonthsInvoiceData = []
          } else {
            this.allMonthsInvoiceData = []
            this.allMonthsWindowKey = scopeKey
          }
        } catch (error) {
          console.error('Error loading all-months invoices:', error)
          if (!this.allMonthsWindowKey) this.allMonthsInvoiceData = []
        }
        return this.allMonthsInvoiceData
      }

      this.allMonthsInFlightKey = scopeKey
      this.allMonthsInFlight = run().finally(() => {
        if (this.allMonthsInFlightKey === scopeKey) {
          this.allMonthsInFlight = null
          this.allMonthsInFlightKey = ''
        }
      })
      return this.allMonthsInFlight
    },

    async ensureAllMonthsInvoices() {
      const key = this.allMonthsScopeKey()
      if (this.allMonthsWindowKey === key && Array.isArray(this.allMonthsInvoiceData)) return
      if (this.allMonthsInFlight && this.allMonthsInFlightKey === key) {
        this.loading = true
        try {
          await this.allMonthsInFlight
        } finally {
          this.loading = false
        }
        return
      }
      this.loading = true
      try {
        await this.loadAllMonthsInvoices()
      } finally {
        this.loading = false
      }
    },

    inferBillingModelFromCategory(category) {
      if (!category) return 'N/A'
      const categoryLower = String(category).toLowerCase()
      if (categoryLower.includes('contractual')) return 'Contractual Staffing'
      if (categoryLower.includes('recruitment')) return 'Permanent Hiring'
      if (categoryLower.includes('payroll')) return 'Payroll'
      if (categoryLower.includes('cost')) return 'Cost Plus'
      return 'N/A'
    },

    async getRecruiterIdsForMyCandidates() {
      if (this.recruiterIdsCache != null) return this.recruiterIdsCache
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const empId = userData.emp_id
      if (this.$can('read', 'admin') || this.$can('read', 'hr') || this.$can('read', 'master') || this.$can('read', 'mis')) {
        const res = await UserService.getAllUsersDdActiveInactive()
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          const recruiters = res.data.filter(u =>
            ['talent-advisor-sourcer', 'account-manager', 'talent-advisor'].includes((u.role || '').toLowerCase())
          )
          this.recruiterIdsCache = recruiters.map(u => u.emp_id || u.id).filter(Boolean).join('|')
          return this.recruiterIdsCache
        }
      }
      if (this.$can('read', 'account_manager')) {
        const res = await UserService.getAllTeamMembersList(empId)
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          this.recruiterIdsCache = res.data.map(u => u.emp_id || u.id).filter(Boolean).join('|')
          return this.recruiterIdsCache
        }
      }
      this.recruiterIdsCache = empId || ''
      return this.recruiterIdsCache
    },

    async loadCandidateRevenue() {
      try {
        // Empty From/To = all history; default/Clear use this month
        const startDate = this.candidateRevenueFilters.startDate || ''
        const endDate = this.candidateRevenueFilters.endDate || ''
        const ids = await this.getRecruiterIdsForMyCandidates()
        const statusFilter = this.candidateRevenueFilters.candidateStatus || ''
        const categoryFilter = this.candidateRevenueFilters.category || ''
        const res = await CandidateService.getMyCandidates(ids, {
          page: 1,
          limit: 5000,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          startDate,
          endDate,
          client: this.candidateRevenueFilters.client || '',
          vertical: this.candidateRevenueFilters.industry || '',
          dateField: 'joining',
          role: '',
          rec: ''
        })
        const candidates = (res && res.status === 'OK' && res.data?.candidates) ? res.data.candidates : []
        const invoicesByCandidateJob = new Map()
        for (const inv of this.invoiceData || []) {
          const invCid = String(inv.candidate_id || inv.candidateId || '').trim()
          const invJid = String(inv.job_id || '').trim()
          if (!invCid || !invJid) continue
          const key = `${invCid}_${invJid}`
          const list = invoicesByCandidateJob.get(key)
          if (list) list.push(inv)
          else invoicesByCandidateJob.set(key, [inv])
        }
        const mapped = candidates.map(c => {
          const rev = parseFloat(c.revenue) || 0
          const posType = (c.position_type || c.billing_model || '').toLowerCase()
          let billingModel = 'Permanent Hiring'
          if (posType.includes('contractual') || posType.includes('staffing')) {
            billingModel = 'Contractual Staffing'
          } else if (posType.includes('cost')) {
            billingModel = 'Cost Plus'
          } else if (this.isFteBillingModel(posType) || !posType) {
            billingModel = 'Permanent Hiring'
          }
          const joiningDateRaw = c.joining_date || c.doj || c.date_of_joining || c.joiningDate || null
          const joinDate = joiningDateRaw ? moment(joiningDateRaw).format('YYYY-MM-01') : null
          const cid = c.cid || c.candidate_id
          const jid = c.jid || c.job_id
          // Resolve invoice(s) - Contractual can have multiple invoices per candidate
          let invoiceNumber = c.invoice_id || c.invoice_number || c.invoiceId
          let invoicesForCandidate = []
          if (cid && jid) {
            invoicesForCandidate = invoicesByCandidateJob.get(`${String(cid).trim()}_${String(jid).trim()}`) || []
            if (!invoiceNumber && invoicesForCandidate.length > 0) {
              invoiceNumber = invoicesForCandidate.map(inv => inv.invoice_id || inv.invoice_number).filter(Boolean).join(', ')
            }
          }
          return {
            candidate_id: cid,
            candidate_name: (c.candidate_name || '').replace(/SHC-\d+ - /g, '').trim() || `Candidate ${cid || 'N/A'}`,
            job_id: jid,
            client_name: c.client || 'N/A',
            position_name: (c.position_name || c.job_title || c.position || '').trim() || null,
            invoice_number: invoiceNumber || 'N/A',
            invoice_id: invoiceNumber,
            _invoicesForCandidate: invoicesForCandidate,
            revenue_amount: rev,
            revenue_confirmed: c.revenue_confirmed || null,
            billing_model: billingModel,
            billing_period: joinDate,
            joining_date: joiningDateRaw,
            // my-candidates returns candidateStatus (same as My Candidates list)
            candidate_status: c.candidateStatus || c.status || c.candidate_status || statusFilter || '',
            category: c.category || c.position_category || categoryFilter || '',
            position_type: c.position_type || null,
            vertical: c.vertical || c.industry || c.department || c.client_vertical || c.job_vertical || '',
            department: c.department || '',
            industry: c.industry || c.vertical || c.department || ''
          }
        })
        // Deduplicate by (candidate_id, job_id) - contractual candidates can be in multiple invoices; show one row
        const seen = new Map()
        this.candidateRevenueData = mapped.filter(row => {
          const key = `${row.candidate_id}_${row.job_id}`
          const existing = seen.get(key)
          if (!existing) {
            seen.set(key, row)
            return true
          }
          // Merge: add invoices and revenue for duplicate (cid, jid)
          const allInvs = [...(existing._invoicesForCandidate || [])]
          for (const inv of (row._invoicesForCandidate || [])) {
            const id = inv.invoice_id || inv.invoice_number
            if (id && !allInvs.some(i => (i.invoice_id || i.invoice_number) === id)) allInvs.push(inv)
          }
          existing._invoicesForCandidate = allInvs
          existing.invoice_number = allInvs.map(i => i.invoice_id || i.invoice_number).filter(Boolean).join(', ') || existing.invoice_number
          existing.revenue_amount = (parseFloat(existing.revenue_amount) || 0) + (parseFloat(row.revenue_amount) || 0)
          return false
        })
        this.markTabLoaded('revenue')
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading candidate revenue:', error)
        this.candidateRevenueData = []
      }
    },

    async openUpdateRevenueModal(item) {
      if (!item) return
      // Contractual/Cost+ White Collar, or Joined FTE / Full Time / Fixed Term
      if (!this.canUpdateCandidateRevenue(item)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Not applicable',
            text: 'Update Revenue is available for Joined FTE (Full Time / Fixed Term) candidates, and for White Collar Contractual candidates.',
            icon: 'InfoIcon',
            variant: 'warning'
          }
        })
        return
      }
      this.selectedRevenueCandidate = { ...item }
      this.resetRevenueModal()
      if (this.isContractualRevenue) {
        if (!this.$can('update', 'contractual_candidates') && !this.$can('create', 'contractual_candidates')) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Permission denied', icon: 'AlertCircleIcon', variant: 'danger' }
          })
          return
        }
        try {
          const [monthlyRes, configRes] = await Promise.all([
            ContractualService.findByCandidate(item.candidate_id, item.job_id),
            ContractualConfigService.findByCandidate(item.candidate_id, item.job_id)
          ])
          this.contractualMonthlyRecords = Array.isArray(monthlyRes) ? monthlyRes : (monthlyRes?.data ? monthlyRes.data : [])
          const configData = configRes?.data?.data
          this.contractualConfigData = Array.isArray(configData) && configData.length ? configData[0] : null
        } catch (e) {
          console.error('Failed to load contractual records:', e)
          this.contractualMonthlyRecords = []
          this.contractualConfigData = null
        }
      } else {
        if (!this.$can('update', 'candidates')) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Permission denied', icon: 'AlertCircleIcon', variant: 'danger' }
          })
          return
        }
        this.permanentRevenueForm.revenue = item.revenue_amount || null
        this.permanentRevenueForm.revenue_confirmed = item.revenue_confirmed || null
      }
      this.$refs.updateRevenueModal?.show(item)
    },

    resetRevenueModal() {
      this.contractualForm = { month: '', revenue: null, start_date: '', end_date: '', invoice_id: '', editId: null }
      this.permanentRevenueForm = { revenue: null, revenue_confirmed: null }
      this.contractualMonthlyRecords = []
      this.contractualConfigData = null
    },

    hideUpdateRevenueModal() {
      this.$refs.updateRevenueModal?.hide()
      this.selectedRevenueCandidate = null
      this.resetRevenueModal()
    },

    async saveContractDetails(form) {
      const c = form.candidate || this.selectedRevenueCandidate
      if (!c || !c.candidate_id || !c.job_id) return
      const cid = c.candidate_id
      const jid = c.job_id
      const data = {
        candidate_id: cid,
        job_id: jid,
        start_date: form.contractStartDate || null,
        end_date: form.contractEndDate || null,
        rate_card: form.rateCard || null,
        monthly_ctc: form.monthlyCTC || null,
        probation_days: form.probationDays || null
      }
      try {
        let res
        if (form.editId) {
          res = await ContractualConfigService.update(cid, jid, data)
        } else {
          res = await ContractualConfigService.create(data)
        }
        if (res && (res.status === 200 || res.status === 201)) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Contract details saved', icon: 'CheckIcon', variant: 'success' }
          })
          const configRes = await ContractualConfigService.findByCandidate(cid, jid)
          const configData = configRes?.data?.data
          this.contractualConfigData = Array.isArray(configData) && configData.length ? configData[0] : null
        } else {
          this.$toast({
            component: ToastificationContent,
            props: { title: res?.data?.message || 'Save failed', icon: 'AlertCircleIcon', variant: 'danger' }
          })
        }
      } catch (e) {
        console.error('saveContractDetails error:', e)
      }
    },

    async savePermanentRevenue(payload = {}) {
      const c = payload.candidate || this.selectedRevenueCandidate
      if (!c || !c.candidate_id || !c.job_id) return
      const rev = parseFloat(payload.revenue != null ? payload.revenue : this.permanentRevenueForm.revenue)
      if (isNaN(rev) || rev < 0) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Enter valid revenue amount', icon: 'AlertCircleIcon', variant: 'warning' }
        })
        return
      }
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const user = `[{"title" : "${userData.first_name || ''} ${userData.last_name || ''}", "emp_id": "${userData.emp_id || ''}"}]`
      const confirmed = payload.revenue_confirmed != null
        ? payload.revenue_confirmed
        : this.permanentRevenueForm.revenue_confirmed
      const update = {
        updatedBy: user,
        revenue: rev,
        revenue_confirmed: confirmed || 'No'
      }
      try {
        const res = await CandidateService.updateCandidateStatus(c.candidate_id, c.job_id, update)
        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Revenue updated successfully', icon: 'CheckIcon', variant: 'success' }
          })
          this.hideUpdateRevenueModal()
          await this.loadCandidateRevenue()
        } else {
          this.$toast({
            component: ToastificationContent,
            props: { title: res?.message || 'Failed to update revenue', icon: 'AlertCircleIcon', variant: 'danger' }
          })
        }
      } catch (err) {
        console.error('Update revenue error:', err)
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Failed to update revenue', icon: 'AlertCircleIcon', variant: 'danger' }
        })
      }
    },

    editContractualRecord(item) {
      this.contractualForm = {
        month: item.month || '',
        revenue: item.revenue != null ? parseFloat(item.revenue) : null,
        start_date: item.start_date || '',
        end_date: item.end_date || '',
        invoice_id: item.invoice_id || '',
        editId: item.id
      }
    },

    async saveContractualRecord(form) {
      const c = form.candidate || this.selectedRevenueCandidate
      if (!c || !c.candidate_id || !c.job_id) return
      const cid = c.candidate_id
      const jid = c.job_id

      if (form.editId) {
        const data = {
          month: form.contractMonth,
          start_date: form.contractStartDate || null,
          end_date: form.contractEndDate || null,
          num_of_days: form.numberOfDays || null,
          rate_card: form.rateCard || null,
          computed_rate_card: form.computedRateCard || null,
          salary: form.empSalary || null,
          computed_salary: form.computedSalary || null,
          revenue: form.revenueGenerated || null,
          invoice_id: form.invoiceId || null
        }
        const res = await ContractualService.update(form.editId, cid, jid, data)
        if (res && res.status === 200) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Monthly record updated', icon: 'CheckIcon', variant: 'success' }
          })
          const list = await ContractualService.findByCandidate(cid, jid)
          this.contractualMonthlyRecords = Array.isArray(list) ? list : (list?.data ? list.data : [])
        } else {
          this.$toast({
            component: ToastificationContent,
            props: { title: res?.data?.message || 'Update failed', icon: 'AlertCircleIcon', variant: 'danger' }
          })
        }
      } else {
        const data = {
          candidate_id: cid,
          job_id: jid,
          month: form.contractMonth,
          start_date: form.contractStartDate || null,
          end_date: form.contractEndDate || null,
          num_of_days: form.numberOfDays || null,
          rate_card: form.rateCard || null,
          computed_rate_card: form.computedRateCard || null,
          salary: form.empSalary || null,
          computed_salary: form.computedSalary || null,
          revenue: form.revenueGenerated || null,
          invoice_id: form.invoiceId || null
        }
        const res = await ContractualService.create(data)
        if (res && res.status === 201) {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Monthly record added', icon: 'CheckIcon', variant: 'success' }
          })
          const list = await ContractualService.findByCandidate(cid, jid)
          this.contractualMonthlyRecords = Array.isArray(list) ? list : (list?.data ? list.data : [])
        } else {
          this.$toast({
            component: ToastificationContent,
            props: { title: res?.data?.message || 'Add failed', icon: 'AlertCircleIcon', variant: 'danger' }
          })
        }
      }
    },

    async loadCollectionsData() {
      try {
        if (!Array.isArray(this.invoiceData)) {
          this.collectionsData = []
          return
        }
        const f = this.collectionsFilters
        const startDate = f.startDate || ''
        const endDate = f.endDate || ''
        const client = f.client || ''
        if (this.canReuseOverviewInvoices(startDate, endDate, client)) {
          this.collectionsData = this.buildCollectionsFromInvoices(
            this.filterInvoicesForWindow(this.invoiceData, startDate, endDate, client)
          )
          this.markTabLoaded('collections')
          return
        }
        await this.loadCollectionsWithFilters()
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading collections data:', error)
        this.collectionsData = []
      }
    },

    buildCollectionsFromInvoices(invoices) {
      if (!Array.isArray(invoices)) return []
      return invoices
        .filter(inv => (inv.status || '').toLowerCase() !== 'paid' && inv.outstanding_amount > 0)
        .map(inv => {
          const dueDate = moment(inv.due_date || inv.dueDate)
          const daysOverdue = dueDate.isValid() ? moment().diff(dueDate, 'days') : 0
          const computedStatus = this.getFollowUpStatus(daysOverdue)
          const savedType = inv.follow_up_type || inv.followUpType || null
          return {
            ...inv,
            client_gst: inv.client_gst || inv.sattva_gst || '',
            days_overdue: daysOverdue > 0 ? daysOverdue : 0,
            follow_up_status: savedType || computedStatus,
            last_follow_up_date: inv.last_follow_up_date || inv.lastFollowUpDate || null
          }
        })
        .sort((a, b) => b.days_overdue - a.days_overdue)
    },

    async loadCollectionsWithFilters() {
      try {
        const startDate = this.collectionsFilters.startDate || ''
        const endDate = this.collectionsFilters.endDate || ''
        const client = this.collectionsFilters.client || ''
        const rawInvoices = await this.getInvoicesForWindow(startDate, endDate, client)
        const invoices = rawInvoices.map(inv => {
          const totalAmount = parseFloat(inv.total_amount) || parseFloat(inv.amount_with_gst) || 0
          const paidAmount = parseFloat(inv.paid_amount) || 0
          return {
            ...inv,
            total_amount: totalAmount,
            paid_amount: paidAmount,
            outstanding_amount: inv.outstanding_amount != null
              ? inv.outstanding_amount
              : Math.max(0, totalAmount - paidAmount),
            due_date: inv.due_date || inv.dueDate,
            status: inv.status,
            client_name: inv.client || inv.client_name || 'N/A',
            invoice_id: inv.invoice_id,
            invoice_number: inv.invoice_id || inv.invoice_number || 'N/A'
          }
        })
        this.collectionsData = this.buildCollectionsFromInvoices(invoices)
        this.markTabLoaded('collections')
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading collections with filters:', error)
        this.collectionsData = []
      }
    },

    mapGstrRows(invoices, period, includeTotal = false) {
      return (invoices || []).map((inv) => {
        const base = parseFloat(inv.base_amount) || 0
        const gst = parseFloat(inv.gst) || 0
        const gstType = (inv.gst_type || '').toLowerCase()
        let cgst = parseFloat(inv.cgst_amount) || 0
        let sgst = parseFloat(inv.sgst_amount) || 0
        let igst = parseFloat(inv.igst_amount) || 0
        if (cgst === 0 && sgst === 0 && igst === 0 && gst > 0) {
          if (gstType.includes('igst')) { igst = gst } else { cgst = gst / 2; sgst = gst / 2 }
        }
        const row = {
          invoice_number: inv.invoice_id || inv.invoice_number || 'N/A',
          invoice_date: inv.invoice_date || '',
          client_name: inv.client || inv.client_name || 'N/A',
          gstin: inv.client_gst || inv.sattva_gst || '',
          taxable_value: base,
          cgst,
          sgst,
          igst
        }
        if (includeTotal) {
          row.total_gst = cgst + sgst + igst
          row.gstr1_period = period
        }
        return row
      })
    },

    mapFinanceRows(invoices) {
      return (invoices || []).map((inv) => {
        const total = parseFloat(inv.total_amount) || parseFloat(inv.amount_with_gst) || 0
        const paid = parseFloat(inv.paid_amount) || 0
        const outstanding = inv.outstanding != null
          ? inv.outstanding
          : (inv.outstanding_amount != null ? inv.outstanding_amount : Math.max(0, total - paid))
        return {
          invoice_id: inv.invoice_id || inv.invoice_number || 'N/A',
          client: inv.client || inv.client_name || 'N/A',
          client_gst: inv.client_gst || inv.sattva_gst || '',
          status: inv.status || 'N/A',
          invoice_date: inv.invoice_date,
          due_date: inv.due_date || inv.dueDate,
          total_amount: total,
          paid_amount: paid,
          outstanding
        }
      })
    },

    async loadGSTR1Data(payload, manageLoading = true) {
      if (this.isFilterPayload(payload)) {
        this.gstr1Filters = {
          ...this.gstr1Filters,
          ...payload
        }
      }
      const gstr1Key = this.currentTabFetchKey('gstr1')
      if (this.tabFetchKey.gstr1 === gstr1Key) return
      if (manageLoading) this.loading = true
      try {
        const period = this.gstr1Filters.period || moment().format('YYYY-MM')
        const [y, m] = period.split('-').map(Number)
        const startDate = `${y}-${String(m).padStart(2, '0')}-01`
        const endDate = moment(`${y}-${String(m).padStart(2, '0')}-01`).endOf('month').format('YYYY-MM-DD')
        const client = this.gstr1Filters.client || ''
        const invoices = await this.getInvoicesForWindow(startDate, endDate, client)
        this.gstr1Data = this.mapGstrRows(invoices, period, true)
        this.markTabLoaded('gstr1')
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading GSTR-1 data:', error)
        this.gstr1Data = []
      } finally {
        if (manageLoading) this.loading = false
      }
    },

    async loadGSTR3BData(payload, manageLoading = true) {
      if (this.isFilterPayload(payload)) {
        this.gstr3bFilters = {
          ...this.gstr3bFilters,
          ...payload
        }
      }
      const gstr3Key = this.currentTabFetchKey('gstr3b')
      if (this.tabFetchKey.gstr3b === gstr3Key) return
      if (manageLoading) this.loading = true
      try {
        const period = this.gstr3bFilters.period || moment().format('YYYY-MM')
        const [y, m] = period.split('-').map(Number)
        const startDate = `${y}-${String(m).padStart(2, '0')}-01`
        const endDate = moment(`${y}-${String(m).padStart(2, '0')}-01`).endOf('month').format('YYYY-MM-DD')
        const client = this.gstr3bFilters.client || ''
        const invoices = await this.getInvoicesForWindow(startDate, endDate, client)
        this.gstr3bData = this.mapGstrRows(invoices, period, false)
        this.gstr3bSummary = this.gstr3bData.reduce((acc, it) => ({
          taxable_value: (acc.taxable_value || 0) + (it.taxable_value || 0),
          cgst: (acc.cgst || 0) + (it.cgst || 0),
          sgst: (acc.sgst || 0) + (it.sgst || 0),
          igst: (acc.igst || 0) + (it.igst || 0)
        }), {})
        this.markTabLoaded('gstr3b')
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading GSTR-3B data:', error)
        this.gstr3bData = []
        this.gstr3bSummary = null
      } finally {
        if (manageLoading) this.loading = false
      }
    },

    async loadClients() {
      try {
        const res = await clientService.getClients()
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          const seen = new Set()
          this.clients = res.data
            .map((client, idx) => {
              const title = String(client.client_name || client.name || client.title || client.client || '').trim() || 'N/A'
              const value = client.id ?? client.client_id ?? `client-${idx}`
              // Keep title for tab filters (match by client name); text/value for b-form-select
              return { title, text: title, value: title, id: value }
            })
            .filter((opt) => {
              if (opt.title === 'N/A' && seen.has('N/A')) return false
              seen.add(opt.title)
              return true
            })
        } else {
          this.clients = []
        }
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading clients:', error)
        this.clients = []
      }
    },

    openOverviewFilters() {
      this.$bvModal.show('invoice-overview-filters')
    },

    closeOverviewFilters() {
      this.$bvModal.hide('invoice-overview-filters')
    },

    applyFilters() {
      this.persistFiltersToSession()
      const nextKey = this.overviewWindowKey()
      const nextAllMonthsKey = this.allMonthsScopeKey()
      const windowChanged = nextKey !== this.invoicesWindowKey
      const allMonthsChanged = nextAllMonthsKey !== this.allMonthsWindowKey
      if (!windowChanged && !allMonthsChanged) {
        this.closeOverviewFilters()
        return
      }
      if (windowChanged) this.invalidateInvoiceDerivedTabs()
      this.closeOverviewFilters()
      this.loading = true
      Promise.all([
        windowChanged ? this.loadInvoices() : Promise.resolve(),
        allMonthsChanged ? this.loadAllMonthsInvoices({ force: true }) : Promise.resolve()
      ])
        .then(() => this.refreshVisitedTabData())
        .catch((error) => {
          console.error('[InvoiceDashboard] Error applying filters:', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    isStatusChipActive(chipValue) {
      if (this.filters.showOverdueOnly) return false
      if (chipValue == null) return !this.filters.status
      return this.invoiceStatusesMatch(this.filters.status, chipValue)
    },

    setStatusQuickFilter(status) {
      this.filters.status = status
      this.filters.showOverdueOnly = false
      this.persistFiltersToSession()
      // Dates stay as-is. Raised / Issued / Part Paid read the cached all-months set.
      if (this.isAllMonthsStatusChip(status)) {
        this.ensureAllMonthsInvoices()
      }
    },

    setOverdueQuickFilter() {
      this.filters.status = null
      this.filters.showOverdueOnly = true
      this.persistFiltersToSession()
      this.ensureAllMonthsInvoices()
    },

    clearFilters() {
      const range = defaultOverviewDateRange()
      const nextKey = makeInvoiceWindowKey(range.startDate, range.endDate, '', '')
      const sameWindow = this.invoicesWindowKey === nextKey
      const sameAllMonths = this.allMonthsWindowKey === 'allMonths||'
      this.filters = {
        status: null,
        billingModel: null,
        client: null,
        category: null,
        industry: null,
        billingMonth: null,
        gstr1Status: null,
        dueBucket: null,
        startDate: range.startDate,
        endDate: range.endDate,
        showOverdueOnly: false,
        candidateFilter: 'all'
      }
      this.filtersExpanded = false
      this.clearPersistedFilters()
      if (!sameWindow) this.invalidateInvoiceDerivedTabs()
      if (!sameWindow || !sameAllMonths) {
        this.loading = true
        Promise.all([
          sameWindow ? Promise.resolve() : this.loadInvoices(),
          sameAllMonths ? Promise.resolve() : this.loadAllMonthsInvoices({ force: true })
        ])
          .then(() => this.refreshVisitedTabData())
          .catch((error) => {
            console.error('[InvoiceDashboard] Error clearing filters:', error)
          })
          .finally(() => {
            this.loading = false
          })
      }
      this.closeOverviewFilters()
    },

    isFilterPayload(payload) {
      return !!(
        payload &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        !(typeof Event !== 'undefined' && payload instanceof Event) &&
        typeof payload.preventDefault !== 'function' &&
        !payload.target
      )
    },

    async applyCandidateRevenueFilters(payload) {
      if (this.isFilterPayload(payload)) {
        this.candidateRevenueFilters = {
          ...this.candidateRevenueFilters,
          ...payload
        }
      }
      const key = this.currentTabFetchKey('revenue')
      if (this.tabFetchKey.revenue === key) return
      this.loading = true
      try {
        await this.loadCandidateRevenue()
      } finally {
        this.loading = false
      }
    },

    clearCandidateRevenueFilters() {
      const range = thisMonthDateRange()
      this.candidateRevenueFilters = {
        startDate: range.startDate,
        endDate: range.endDate,
        client: null,
        industry: null,
        candidateStatus: '',
        category: ''
      }
      this.tabFetchKey.revenue = ''
      this.applyCandidateRevenueFilters()
    },

    async applyCollectionsFilters(payload) {
      if (this.isFilterPayload(payload)) {
        this.collectionsFilters = {
          ...this.collectionsFilters,
          ...payload
        }
      }
      const key = this.currentTabFetchKey('collections')
      if (this.tabFetchKey.collections === key) return
      this.loading = true
      try {
        const f = this.collectionsFilters
        if (this.canReuseOverviewInvoices(f.startDate || '', f.endDate || '', f.client || '')) {
          this.collectionsData = this.buildCollectionsFromInvoices(
            this.filterInvoicesForWindow(this.invoiceData, f.startDate || '', f.endDate || '', f.client || '')
          )
          this.markTabLoaded('collections')
        } else {
          await this.loadCollectionsWithFilters()
        }
      } finally {
        this.loading = false
      }
    },

    clearCollectionsFilters() {
      const range = thisMonthDateRange()
      this.collectionsFilters = {
        startDate: range.startDate,
        endDate: range.endDate,
        client: null
      }
      this.tabFetchKey.collections = ''
      this.applyCollectionsFilters()
    },

    applyContractualFilters(payload) {
      if (this.isFilterPayload(payload)) {
        this.contractualFilters = {
          ...this.contractualFilters,
          ...payload
        }
      }
      const key = this.currentTabFetchKey('contractual')
      if (this.tabFetchKey.contractual === key) return
      this.loadContractualInvoices()
    },

    clearContractualFilters() {
      this.contractualFilters = { client: null }
      this.loadContractualInvoices()
    },

    viewInvoiceDetails(item) {
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.selectedInvoiceForView = item
      this.$nextTick(() => {
        this.$bvModal.show('modal-invoice-view')
      })
    },

    viewInvoiceHistory(item) {
      if (!item || (!item.id && !item.invoice_id)) return
      this.selectedInvoiceForHistory = item
      this.$nextTick(() => {
        this.$bvModal.show('modal-invoice-revision-history')
      })
    },

    notifyDownloadPermissionDenied() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Permission denied',
          text: INVOICE_DOWNLOAD_DENIED_TOOLTIP,
          icon: 'AlertCircleIcon',
          variant: 'danger',
        },
      })
    },

    async downloadInvoicePdf(item) {
      if (!this.canDownloadInvoices) {
        this.notifyDownloadPermissionDenied()
        return
      }
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      const id = resolveInvoiceApiKey(item)
      try {
        const pdfRes = await enhancedInvoiceService.downloadInvoicePdf(id)
        if (pdfRes && pdfRes.status === 'OK' && pdfRes.blob) {
          const url = URL.createObjectURL(pdfRes.blob)
          const link = document.createElement('a')
          link.href = url
          // Same pattern as Create → Download invoice in template:
          // `{Client} {Invoice|Proforma|Credit Note|Debit Note} {ID} ({Mon-YYYY}).pdf`
          link.download = buildInvoiceDownloadFilename(item)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Downloaded',
              text: 'Invoice PDF downloaded.',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
          return
        }
        // Open detail modal so user can use full workflow (incl. stored file fallback)
        this.viewInvoiceDetails(item)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'PDF from server unavailable',
            text: (pdfRes && pdfRes.message) || 'Open the invoice to download a stored copy, or ask backend to implement GET /invoice/:id/pdf.',
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Download failed',
            text: 'Could not download invoice PDF.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    openSignedInvoiceUpload(item) {
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.signedUploadTarget = item
      this.$nextTick(() => {
        this.$bvModal.show('modal-upload-signed-invoice')
      })
    },

    onSignedInvoiceUploaded({ invoice } = {}) {
      const target = invoice || this.signedUploadTarget || this.selectedInvoiceForView
      if (target) {
        this.$set(target, 'has_signed_invoice', true)
        const id = target.id || target.invoice_id
        this.patchInvoiceCaches(id, (row) => {
          this.$set(row, 'has_signed_invoice', true)
        })
      }
      // Keep existing invoice status (Raised / Issued / …); signed copy is tracked via attachments + flag.
      this.refreshAfterInvoiceChange()
    },

    async downloadInvoiceOverviewExcel(item) {
      if (!this.canDownloadInvoices) {
        this.notifyDownloadPermissionDenied()
        return
      }
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Nothing to export for this row.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      try {
        let invoice = item
        try {
          const res = await invoiceService.getById(item.invoice_id || item.id)
          const payload = res && res.status === 'OK' ? res.data : null
          const data = payload && payload.data != null && typeof payload.data === 'object' && !Array.isArray(payload.data)
            ? payload.data
            : payload
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            invoice = { ...item, ...data }
          }
        } catch (hydrateErr) {
          console.warn('[InvoiceDashboard] excel hydrate skipped:', hydrateErr)
        }
        const filename = downloadEInvoiceExcel(invoice)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Downloaded',
            text: `E-Invoice Excel saved (${filename}).`,
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })
      } catch (err) {
        console.error('[InvoiceDashboard] download excel failed:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Could not build the Excel file.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    downloadInvoiceOverviewJson(item) {
      if (!this.canDownloadInvoices) {
        this.notifyDownloadPermissionDenied()
        return
      }
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Nothing to export for this row.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      const filename = buildEInvoiceDownloadFilename(item, 'json')
      // Official GST e-invoice Version 1.1 document (single object for per-row download)
      const payload = buildEInvoiceDocument(item)
      const dataStr = JSON.stringify(payload, null, 2)
      const blob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Downloaded',
          text: `E-Invoice JSON saved (${filename}).`,
          icon: 'CheckCircleIcon',
          variant: 'success'
        }
      })
    },

    goToUpdateInvoice(invoice) {
      this.$bvModal.hide('modal-invoice-view')
      this.selectedInvoiceForView = null
      const invoiceId = resolveInvoiceApiKey(invoice)
      if (invoiceId) {
        this.$router.push({ name: 'apps-update-invoice', params: { id: invoiceId } })
      }
    },

    recordPayment(item) {
      const invoiceKey = item && (item.id || item.invoice_id)
      if (!item || !invoiceKey) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.$refs.recordPaymentModal && this.$refs.recordPaymentModal.show(item)
    },

    async savePayment(formData) {
      try {
        const invoiceKey = formData.invoice_id
        const inv = this.findCachedInvoice(invoiceKey)
        const totalAmount = parseFloat(inv?.total_amount) || parseFloat(inv?.amount_with_gst) || parseFloat(formData.total_amount) || 0
        const previousPaid = parseFloat(inv?.paid_amount) || 0
        const paymentAmount = parseFloat(formData.amount) || 0
        const paidAmount = Number((previousPaid + paymentAmount).toFixed(2))
        const status = totalAmount > 0 && paidAmount >= totalAmount
          ? 'Paid'
          : (paidAmount > 0 ? 'Part Paid' : (inv?.status || 'Unpaid'))

        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        // Modal sets these only when amount < outstanding; null for full remaining payment
        const shortReason = formData.short_payment_reason || formData.payment_hold_reason || null
        const shortReasonLabel = formData.short_payment_reason_label || formData.hold_reason || null

        const payload = {
          invoice_id: invoiceKey,
          payment_date: formData.payment_date,
          amount: paymentAmount,
          payment_mode: formData.payment_mode,
          reference_number: formData.reference_number || '',
          notes: formData.notes || '',
          paid_amount: paidAmount,
          amount_paid: paidAmount,
          status,
          paid_date: formData.payment_date || moment().format('YYYY-MM-DD'),
          updated_by: userData.emp_id || '',
          // Short / partial payment reason (dedicated + legacy hold_reason)
          short_payment_reason: shortReason,
          short_payment_reason_label: shortReasonLabel,
          payment_hold_reason: shortReason,
          hold_reason: shortReasonLabel
        }

        const res = await enhancedInvoiceService.recordPayment(payload)
        if (res && res.status === 'OK') {
          // Ensure invoice row paid_amount/status stay in sync even if payments API
          // only stores the transaction and does not update the invoice header.
          try {
            await invoiceService.updateInvoice(invoiceKey, {
              status,
              paid_amount: paidAmount,
              paid_date: payload.paid_date,
              updated_by: payload.updated_by
            })
          } catch (syncErr) {
            console.warn('[InvoiceDashboard] paid_amount sync after payment failed', syncErr)
          }
          this.patchInvoiceCaches(invoiceKey, (row) => {
            this.$set(row, 'paid_amount', paidAmount)
            this.$set(row, 'outstanding_amount', Math.max(0, totalAmount - paidAmount))
            this.$set(row, 'status', status)
            this.$set(row, 'paid_date', payload.paid_date)
          })
          this.$bvToast.toast('Payment recorded successfully', { title: 'Success', variant: 'success', solid: true })
          this.$refs.recordPaymentModal && this.$refs.recordPaymentModal.hide()
          this.refreshAfterInvoiceChange()
        } else {
          throw new Error((res && res.message) || 'Failed to record payment')
        }
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to record payment', { title: 'Error', variant: 'danger', solid: true })
      }
    },

    updateStatus(item) {
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.$refs.updateStatusModal && this.$refs.updateStatusModal.show(item)
    },

    async openReviseEditor({ invoice_id: invoiceId, r1_filed: r1Filed }) {
      if (!invoiceId) return
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        // Persist Revised immediately (JSON) — same path as Paid/Part Paid.
        // Dashboard Continue used to only navigate; invoices.status never changed
        // until (or unless) the full Update Invoice FormData save stuck.
        const updateData = {
          status: REVISED_STATUS,
          r1_filed: r1Filed || '',
          updated_by: userData.emp_id || '',
        }
        const res = await invoiceService.updateInvoice(invoiceId, updateData)
        if (!res || res.status !== 'OK') {
          throw new Error((res && res.message) || 'Failed to set status to Revised')
        }

        const returned = extractUpdatedInvoiceStatus(res)
        let liveStatus = returned
        if (!isRevisedStatusValue(liveStatus)) {
          const got = await invoiceService.getById(invoiceId)
          if (got && got.status === 'OK' && got.data) liveStatus = got.data.status
        }
        if (!isRevisedStatusValue(liveStatus)) {
          throw new Error(
            liveStatus != null
              ? `Server left status as "${liveStatus}" instead of Revised. Backend must UPDATE invoices.status on PUT /invoice/update/:id when status=Revised.`
              : 'Could not verify status=Revised after update. Backend must set invoices.status and return the invoice (or support GET /invoice/:id).',
          )
        }

        this.patchInvoiceCaches(invoiceId, (row) => {
          this.$set(row, 'status', REVISED_STATUS)
        })

        this.$refs.updateStatusModal && this.$refs.updateStatusModal.hide()
        this.$bvToast.toast(`Status updated to "${REVISED_STATUS}"`, { title: 'Success', variant: 'success', solid: true })
        this.$router.push({
          name: 'apps-update-invoice',
          params: { id: invoiceId },
          query: {
            revise: '1',
            ...(r1Filed ? { r1_filed: r1Filed } : {})
          }
        })
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to set status to Revised', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      }
    },

    async saveStatusUpdate(payload) {
      try {
        const invoiceKey = payload.invoice_id
        if (!invoiceKey) throw new Error('Invoice id is required')

        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const shortReason = payload.short_payment_reason || payload.payment_hold_reason || null
        const shortReasonLabel = payload.short_payment_reason_label || payload.hold_reason || null
        const updateData = {
          status: payload.status,
          updated_by: userData.emp_id || '',
          comment: payload.comment || '',
          short_payment_reason: shortReason,
          short_payment_reason_label: shortReasonLabel,
          payment_hold_reason: shortReason,
          hold_reason: shortReasonLabel
        }
        if (payload.paid_date != null) updateData.paid_date = payload.paid_date
        if (payload.paid_amount != null && payload.paid_amount !== '') {
          updateData.paid_amount = Number(payload.paid_amount)
        }

        const res = await invoiceService.updateInvoice(invoiceKey, updateData)
        if (res && res.status === 'OK') {
          this.patchInvoiceCaches(invoiceKey, (row) => {
            this.$set(row, 'status', updateData.status)
            if (updateData.paid_amount != null) {
              const total = parseFloat(row.total_amount) || 0
              this.$set(row, 'paid_amount', updateData.paid_amount)
              this.$set(row, 'outstanding_amount', Math.max(0, total - updateData.paid_amount))
            }
            if (updateData.paid_date) this.$set(row, 'paid_date', updateData.paid_date)
          })
          this.$bvToast.toast(`Status updated to "${updateData.status}"`, { title: 'Success', variant: 'success', solid: true })
          this.$refs.updateStatusModal && this.$refs.updateStatusModal.hide()
          this.refreshAfterInvoiceChange()
        } else {
          throw new Error((res && res.message) || 'Failed to update status')
        }
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to update status', { title: 'Error', variant: 'danger', solid: true })
      }
    },

    openGstrModal(item) {
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.$refs.updateGstrModal && this.$refs.updateGstrModal.show(item)
    },

    async saveGstrUpdate(payload) {
      try {
        const invoiceKey = payload.invoice_id
        if (!invoiceKey) throw new Error('Invoice id is required')

        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const updateData = {
          r1_filed: payload.r1_filed,
          gstr1_status: payload.gstr1_status,
          gstr1_filed: payload.gstr1_filed === true,
          updated_by: userData.emp_id || '',
          comment: payload.comment || ''
        }
        if (payload.gstr1_period) {
          updateData.gstr1_period = payload.gstr1_period
          updateData.billing_month = payload.gstr1_period
        }

        const res = await invoiceService.updateInvoice(invoiceKey, updateData)
        if (res && res.status === 'OK') {
          this.patchInvoiceCaches(invoiceKey, (row) => {
            this.$set(row, 'r1_filed', updateData.r1_filed)
            this.$set(row, 'gstr1_status', updateData.gstr1_status)
            this.$set(row, 'gstr1_filed', updateData.gstr1_filed)
            if (updateData.gstr1_period) {
              this.$set(row, 'gstr1_period', updateData.gstr1_period)
              this.$set(row, 'billing_month', updateData.billing_month)
            }
            if (updateData.comment) this.$set(row, 'comment', updateData.comment)
          })
          this.$bvToast.toast(
            `GSTR updated — R1 ${updateData.r1_filed}, status "${updateData.gstr1_status}"`,
            { title: 'Success', variant: 'success', solid: true }
          )
          this.$refs.updateGstrModal && this.$refs.updateGstrModal.hide()
          this.refreshAfterInvoiceChange()
        } else {
          throw new Error((res && res.message) || (res && res.data && res.data.message) || 'Failed to update GSTR')
        }
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to update GSTR', { title: 'Error', variant: 'danger', solid: true })
      }
    },

    addFollowUp(item) {
      if (!item || (!item.id && !item.invoice_id)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }
      this.$refs.addFollowUpModal && this.$refs.addFollowUpModal.show(item)
    },

    async saveFollowUp(formData) {
      try {
        const invoiceKey = resolveInvoiceApiKey(formData) || formData.invoice_id
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const payload = {
          ...formData,
          invoice_id: invoiceKey,
          next_follow_up: formData.next_follow_up_date || formData.next_follow_up || null,
          done_by: userData.emp_id || userData.employee_id || ''
        }
        const res = await enhancedInvoiceService.addFollowUp(payload)
        if (res && res.status === 'OK') {
          const saved = res.data || {}
          const lastDate = saved.follow_up_date || formData.follow_up_date
          const followType = saved.follow_up_type || formData.follow_up_type
          this.patchInvoiceCaches(invoiceKey, (row) => {
            this.$set(row, 'last_follow_up_date', lastDate)
            this.$set(row, 'follow_up_type', followType)
          })
          this.$bvToast.toast('Follow-up added successfully', { title: 'Success', variant: 'success', solid: true })
          this.$refs.addFollowUpModal && this.$refs.addFollowUpModal.hide()
          await this.loadCollectionsData()
        } else {
          throw new Error(res?.message || 'Failed to add follow-up')
        }
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to add follow-up', { title: 'Error', variant: 'danger', solid: true })
      }
    },

    viewCandidateDetails(candidateId) {
      if (candidateId) {
        this.$router.push({ name: 'apps-candidates-view', params: { id: candidateId } })
      }
    },

    goToCreateInvoice() {
      this.$nextTick(() => this.$router.push({ name: 'apps-invoice-create-manual' }))
    },

    /**
     * Clone an existing invoice into Create Manual for the next billing month.
     * Prefill is handled by InvoiceCreateManual via ?duplicateFrom=<invoiceId>.
     */
    duplicateInvoiceForNextMonth(item) {
      if (!this.$can('create', 'invoice')) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Permission denied',
            text: 'You do not have permission to create invoices.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      const invoiceId = (item && (item.invoice_id || item.invoice_number || item.id)) || null
      if (!invoiceId) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Invalid invoice data',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }
      this.$bvModal.hide('modal-invoice-view')
      this.selectedInvoiceForView = null
      this.$nextTick(() => {
        this.$router.push({
          name: 'apps-invoice-create-manual',
          query: { duplicateFrom: String(invoiceId) },
        })
      })
    },

    showExportModal() {
      if (!this.canDownloadInvoices) {
        this.notifyDownloadPermissionDenied()
        return
      }
      // Implement export functionality
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Info',
          text: 'Export functionality will be implemented',
          icon: 'InfoIcon',
          variant: 'info'
        }
      })
    },

    exportGSTR1() {
      // Implement GSTR-1 export
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Info',
          text: 'GSTR-1 export functionality will be implemented',
          icon: 'InfoIcon',
          variant: 'info'
        }
      })
    },

    exportGSTR3B() {
      // Implement GSTR-3B export
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Info',
          text: 'GSTR-3B export functionality will be implemented',
          icon: 'InfoIcon',
          variant: 'info'
        }
      })
    },

    // Utility methods
    formatIndianNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return '0'
      return parseFloat(num).toLocaleString('en-IN', { maximumFractionDigits: 2 })
    },

    formatDate(date) {
      if (!date) return 'N/A'
      try {
        return moment(date).format('DD-MMM-YYYY')
      } catch (error) {
        return 'Invalid Date'
      }
    },

    isOverdue(dueDate) {
      if (!dueDate) return false
      try {
        return moment(dueDate).isBefore(moment(), 'day')
      } catch (error) {
        return false
      }
    },

    getGstr1StatusVariant(status) {
      const variants = {
        'Not Ready': 'secondary',
        'Ready': 'info',
        'Included': 'primary',
        'Uploaded': 'warning',
        'Filed': 'success'
      }
      return variants[status] || 'secondary'
    },

    normalizeEinvoiceFiled(value) {
      if (value === true || value === 1) return true
      if (value === false || value === 0) return false
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (normalized === 'true' || normalized === 'yes' || normalized === 'filed') return true
        if (normalized === 'false' || normalized === 'no' || normalized === 'not filed') return false
      }
      return null
    },

    openDisputeModal(item) {
      this.$nextTick(() => {
        if (this.$refs.disputeModal) this.$refs.disputeModal.show(item)
      })
    },

    async saveDispute(formData) {
      try {
        const invoiceKey = formData.invoice_id || formData.invoice_number
        if (!invoiceKey) throw new Error('Invoice id is required')

        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const updateData = {
          status: 'Disputed',
          updated_by: userData.emp_id || '',
          comment: formData.notes,
          hold_reason: formData.reason,
          metadata: {
            dispute_reason: formData.reason,
            dispute_notes: formData.notes,
            dispute_resolution_date: formData.resolution_date || null,
            disputed_at: new Date().toISOString()
          }
        }

        const res = await invoiceService.updateInvoice(invoiceKey, updateData)
        if (!res || res.status !== 'OK') {
          throw new Error((res && res.message) || 'Failed to mark dispute')
        }

        this.patchInvoiceCaches(invoiceKey, (row) => {
          this.$set(row, 'status', 'Disputed')
          this.$set(row, 'comment', formData.notes)
        })
        this.$bvToast.toast(`Invoice ${formData.invoice_number} marked as Disputed.`, { title: 'Success', variant: 'success', solid: true })
        if (this.$refs.disputeModal) this.$refs.disputeModal.hide()
      } catch (err) {
        const msg = (err && err.message) || 'Failed to mark dispute. Please try again.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },

    openEInvoiceModal(item) {
      this.$nextTick(() => {
        if (this.$refs.eInvoiceStatusModal) this.$refs.eInvoiceStatusModal.show(item)
      })
    },

    async saveEInvoiceStatus({ invoice, payload }) {
      try {
        const invoiceId = invoice.invoice_id || invoice.id
        await enhancedInvoiceService.updateEInvoiceFilingStatus(invoiceId, payload.status, {
          filing_date: payload.filingDate,
          reason: payload.reason,
          comments: payload.comments
        })
        this.patchInvoiceCaches(invoiceId, (row) => {
          row.einvoice_filed = payload.status === true
          row.einvoice_date = payload.status === true ? (payload.filingDate || null) : null
        })
        this.$bvToast.toast(
          `E-Invoice status updated to "${payload.status ? 'Filed' : 'Not Filed'}" for ${invoiceId}.`,
          { title: 'E-Invoice Updated', variant: payload.status ? 'success' : 'warning', solid: true }
        )
      } catch (err) {
        const msg = (err && err.message) || 'Failed to update e-invoice status. Please try again.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      }
    },

    getStatusVariant(status) {
      const variants = {
        'Draft': 'secondary',
        'Pending Approval': 'warning',
        'Approved': 'info',
        'Issued': 'primary',
        'Raised': 'primary',
        'Part Paid': 'warning',
        'Partially Paid': 'warning',
        'Paid': 'success',
        'Unpaid': 'warning',
        'Overdue': 'danger',
        'Disputed': 'danger',
        'Cancelled': 'dark',
        'Revised': 'info',
        'Early Attrition': 'secondary',
        'Returned': 'warning'
      }
      return variants[status] || 'secondary'
    },

    /**
     * True when a string looks like serialized revenue/candidate JSON
     * (e.g. [{"cid":"…","jid":"…","base_amount":…}]) — never show to users.
     */
    looksLikeTechnicalPayload(value) {
      if (value == null) return false
      if (typeof value === 'object') {
        const rows = Array.isArray(value) ? value : [value]
        return rows.length > 0 && rows.every(row => this.isTechnicalCandidateRow(row))
      }
      const raw = String(value).trim()
      if (!raw || raw === 'null') return false
      if (!(raw.startsWith('[') || raw.startsWith('{'))) {
        // String(arrayOfObjects) → "[object Object]" — also not user-facing
        return raw === '[object Object]' || /^\[object Object\](,\[object Object\])*$/.test(raw)
      }
      try {
        const parsed = JSON.parse(raw)
        const rows = Array.isArray(parsed) ? parsed : [parsed]
        return rows.length > 0 && rows.every(row => this.isTechnicalCandidateRow(row))
      } catch (e) {
        return /"cid"\s*:/.test(raw) && /"jid"\s*:/.test(raw)
      }
    },

    /** Revenue-tag row with cid/jid/base_amount and no human-readable name. */
    isTechnicalCandidateRow(row) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) return false
      const hasName = !!(
        row.CANDIDATE_NAME || row.candidate_name || row.candidateName
        || row.name || row.NAME
      )
      if (hasName) return false
      const hasCid = row.cid != null || row.CAN_ID != null || row.candidate_id != null
      const hasJid = row.jid != null || row.JOB_ID != null || row.job_id != null
      const hasBase = row.base_amount != null || row.baseAmount != null
      return hasCid && (hasJid || hasBase)
    },

    /**
     * Pull a human name (or null) from candidates array / JSON string.
     * Ignores pure revenue-tag payloads ({cid,jid,base_amount}).
     */
    extractCandidateFromCandidatesField(candidates) {
      if (candidates == null || candidates === '') return null
      let rows = candidates
      if (typeof candidates === 'string') {
        const raw = candidates.trim()
        if (!raw || raw === 'null') return null
        if (this.looksLikeTechnicalPayload(raw)) return null
        if (raw.startsWith('[') || raw.startsWith('{')) {
          try {
            const parsed = JSON.parse(raw)
            rows = Array.isArray(parsed) ? parsed : [parsed]
          } catch (e) {
            // Plain human string (e.g. "SHC-123 - John Doe")
            return { name: raw, id: null }
          }
        } else {
          return { name: raw, id: null }
        }
      }
      if (!Array.isArray(rows)) rows = [rows]
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue
        if (this.isTechnicalCandidateRow(row)) continue
        const name = (
          row.CANDIDATE_NAME || row.candidate_name || row.candidateName
          || row.name || row.NAME || ''
        ).toString().trim()
        const id = row.cid || row.CAN_ID || row.candidate_id || null
        if (name || id) return { name: name || null, id }
      }
      return null
    },

    /**
     * Returns display text for candidate. Handles multiple API formats:
     * - Flat: candidate_id, candidate_name
     * - candidateList: array with CAN_ID, CANDIDATE_NAME, cid, jid (from invoice API)
     * - candidates: human string, or array/JSON with names — never raw cid/jid/base_amount JSON
     * - description: human fallback only (skip technical JSON)
     * @param {Object} inv - Invoice item
     * @returns {{ display: string, linkable: boolean }}
     */
    getCandidateDisplay(inv) {
      let name = (inv.candidate_name || inv.candidateName || '').trim()
      let id = inv.candidate_id || inv.candidateId

      // API may return candidateList (array) - same as InvoiceTracker
      if (!name && !id && inv.candidateList && Array.isArray(inv.candidateList) && inv.candidateList.length > 0) {
        const first = inv.candidateList[0]
        name = (first.CANDIDATE_NAME || first.candidate_name || '').trim()
        id = first.cid || first.CAN_ID || first.candidate_id
      }

      // candidates field: may be a name string, or revenue JSON — never dump JSON
      if (!name && !id && inv.candidates != null && inv.candidates !== '') {
        const extracted = this.extractCandidateFromCandidatesField(inv.candidates)
        if (extracted) {
          name = (extracted.name || '').trim()
          id = extracted.id || id
        }
      }

      // Human description only (e.g. "SHC-123 - John Doe") — skip technical payloads
      if (!name && !id && inv.description) {
        const raw = String(inv.description || '').trim()
        if (raw && raw !== 'null' && !this.looksLikeTechnicalPayload(raw)) {
          return { display: raw, linkable: false }
        }
      }

      if (name) return { display: name.replace(/^SHC-\d+\s*-\s*/i, '').trim() || name, linkable: true }
      if (id) return { display: `Not in ATS (${id})`, linkable: false }
      return { display: '—', linkable: false }
    },

    getOverdueVariant(days) {
      if (days <= 0) return 'success'
      if (days <= 7) return 'warning'
      if (days <= 15) return 'danger'
      return 'dark'
    },

    getFollowUpVariant(status) {
      const variants = {
        'Friendly Reminder': 'info',
        'Payment Reminder': 'warning',
        'Escalation': 'danger',
        'Finance Escalation': 'dark'
      }
      return variants[status] || 'secondary'
    },

    getFollowUpStatus(daysOverdue) {
      if (daysOverdue <= 0) return 'No Follow-up Needed'
      if (daysOverdue <= 3) return 'Friendly Reminder'
      if (daysOverdue <= 7) return 'Payment Reminder'
      if (daysOverdue <= 15) return 'Escalation'
      return 'Finance Escalation'
    },

    // Helper method to infer billing model from category
    inferBillingModelFromCategory(category) {
      if (!category) return 'N/A'
      const categoryLower = category.toLowerCase()
      if (categoryLower.includes('contractual')) return 'Contractual Staffing'
      if (categoryLower.includes('recruitment')) return 'Permanent Hiring'
      if (categoryLower.includes('payroll')) return 'Payroll'
      if (categoryLower.includes('cost')) return 'Cost Plus'
      return 'N/A'
    },

    /**
     * Short display labels for billing model: FTE, Contractual, Cost+, Payroll
     */
    getInvoiceForCandidate(candidateRow) {
      const arr = this.getInvoicesForCandidate(candidateRow)
      return arr.length > 0 ? arr[0] : null
    },
    getInvoicesForCandidate(candidateRow) {
      if (candidateRow?._invoicesForCandidate && Array.isArray(candidateRow._invoicesForCandidate)) {
        return candidateRow._invoicesForCandidate
      }
      if (!this.invoiceData || !candidateRow?.candidate_id || !candidateRow?.job_id) return []
      return this.invoiceData.filter(inv => {
        const invCid = String(inv.candidate_id || inv.candidateId || '').trim()
        const invJid = String(inv.job_id || '').trim()
        const cCid = String(candidateRow.candidate_id || '').trim()
        const cJid = String(candidateRow.job_id || '').trim()
        return invCid && invJid && invCid === cCid && invJid === cJid
      })
    },

    getBillingModelShortLabel(model) {
      if (!model) return 'N/A'
      const m = String(model).toLowerCase()
      if (this.isFteBillingModel(m)) return 'FTE'
      if (m.includes('contractual') || m.includes('staffing')) return 'Contractual'
      if (m.includes('cost')) return 'Cost+'
      if (m.includes('payroll')) return 'Payroll'
      return model
    },

    // Finance Dashboard - derived from invoice API
    async loadFinanceDashboard(payload, manageLoading = true) {
      if (this.isFilterPayload(payload)) {
        this.financeFilters = {
          ...this.financeFilters,
          ...payload
        }
      }
      const financeKey = this.currentTabFetchKey('finance')
      if (this.tabFetchKey.finance === financeKey) return
      if (manageLoading) this.loading = true
      try {
        const startDate = this.financeFilters.startDate || ''
        const endDate = this.financeFilters.endDate || ''
        const client = this.financeFilters.client || ''
        const invoices = await this.getInvoicesForWindow(startDate, endDate, client)
        this.financeDashboardData = this.mapFinanceRows(invoices)
        this.markTabLoaded('finance')
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading finance dashboard:', error)
        this.financeDashboardData = []
      } finally {
        if (manageLoading) this.loading = false
      }
    },

    // Recruiter Dashboard - derived from my-candidates API
    async loadRecruiterDashboard(payload, manageLoading = true) {
      if (this.isFilterPayload(payload)) {
        this.recruiterFilters = {
          ...this.recruiterFilters,
          ...payload
        }
      }
      const tabKey = this.currentTabFetchKey('recruiter')
      if (this.tabFetchKey.recruiter === tabKey) return
      if (manageLoading) this.loading = true
      try {
        const startDate = this.recruiterFilters.startDate || ''
        const endDate = this.recruiterFilters.endDate || ''
        const ids = await this.getRecruiterIdsForMyCandidates()
        const res = await CandidateService.getMyCandidates(ids, {
          page: 1,
          limit: 5000,
          status: 'Joined',
          startDate,
          endDate,
          dateField: 'joining',
          role: '',
          rec: ''
        })
        const candidates = (res && res.status === 'OK' && res.data?.candidates) ? res.data.candidates : []
        const byRecruiter = {}
        candidates.forEach(c => {
          const recKey = c.rec_id || c.recruiter || c.sourcer || 'Unknown'
          const recName = c.recruiter || c.sourcer || recKey
          if (!byRecruiter[recKey]) {
            byRecruiter[recKey] = { recruiter_id: recKey, recruiter_name: recName, revenue: 0, placements_billed: 0, clients: new Set() }
          }
          byRecruiter[recKey].revenue += parseFloat(c.revenue) || 0
          byRecruiter[recKey].placements_billed += 1
          if (c.client) byRecruiter[recKey].clients.add(c.client)
        })
        this.recruiterDashboardData = Object.values(byRecruiter).map(r => ({
          recruiter_id: r.recruiter_id,
          recruiter_name: r.recruiter_name,
          revenue: r.revenue,
          placements_billed: r.placements_billed,
          clients_count: r.clients.size,
          avg_revenue_per_placement: r.placements_billed > 0 ? Math.round(r.revenue / r.placements_billed) : 0
        })).sort((a, b) => b.revenue - a.revenue)
        this.totalRows = this.recruiterDashboardData.length
        this.markTabLoaded('recruiter')
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading recruiter dashboard:', error)
        this.recruiterDashboardData = []
        this.totalRows = 0
      } finally {
        if (manageLoading) this.loading = false
      }
    },

    async loadRecruiters() {
      try {
        // Use same API as InvoiceTracker
        const res = await UserService.getAllUsersDdActiveInactive()
        
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          // Use same filter logic as InvoiceTracker (ensure option text is a string)
          this.recruiters = res.data
            .filter(user => 
              user.role === 'talent-advisor-sourcer' || 
              user.role === 'account-manager' || 
              user.role === 'talent-advisor'
            )
            .map(user => ({
              title: String(user.name || user.emp_name || 'N/A'),
              value: user.emp_id || user.id
            }))
        } else {
          this.recruiters = []
        }
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading recruiters:', error)
        this.recruiters = []
      }
    },

    async loadContractualInvoices() {
      try {
        const client = this.contractualFilters.client || ''
        // Empty sd/ed/status omitted by invoiceService.getAll; only category + dateType (+ client when set)
        const res = await invoiceService.getAll(
          '',
          '',
          '',
          client,
          'Contractual',
          'invoice_date'
        )
        if (res && res.status === 'OK') {
          // Handle both res.data (array) and res.data.data (nested array) formats
          const invoices = Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
          this.contractualInvoices = invoices
            .filter(inv => 
              inv.category === 'Contractual' &&
              (inv.status === 'Draft' || inv.status === 'Approved' || inv.status === 'Raised')
            )
            .map(inv => ({
              id: inv.id || inv.invoice_id,
              invoice_id: String(inv.invoice_id || inv.invoice_number || 'N/A'),
              invoice_number: inv.invoice_id || inv.invoice_number || 'N/A',
              client: String(inv.client || 'N/A'),
              status: String(inv.status || '')
            }))
          this.markTabLoaded('contractual')
        } else {
          this.contractualInvoices = []
          if (res && (res.status === 500 || res.status >= 400)) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                text: (res.data && res.data.message) || 'Failed to load contractual invoices. Please try again.',
                icon: 'AlertCircleIcon',
                variant: 'danger'
              }
            })
          }
        }
      } catch (error) {
        console.error('[InvoiceDashboard] Error loading contractual invoices:', error)
        this.contractualInvoices = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load contractual invoices. Please try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },

    async uploadBillingSheet(formPayload) {
      const form = this.isFilterPayload(formPayload)
        ? formPayload
        : this.billingSheetForm
      const rawInvoiceKey = form.invoice_id
      const file = form.file
      if (!rawInvoiceKey || !file) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please select an invoice and upload a file',
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
        return
      }

      const selected = (this.contractualInvoices || []).find(inv =>
        String(inv.id) === String(rawInvoiceKey)
        || String(inv.invoice_id) === String(rawInvoiceKey)
        || String(inv.invoice_number) === String(rawInvoiceKey)
      )
      // ATS invoice routes key by business invoice_id (SH1503), not dashboard PK (1812)
      const businessKey = selected && [selected.invoice_id, selected.invoice_number].find(v => {
        const s = v == null ? '' : String(v).trim()
        return s && s !== 'N/A' && !/^\d+$/.test(s)
      })
      const invoiceKey = businessKey || resolveInvoiceApiKey(selected) || resolveInvoiceApiKey(rawInvoiceKey)

      this.billingSheetForm = { invoice_id: invoiceKey, file }
      this.uploadingSheet = true
      try {
        const res = await enhancedInvoiceService.uploadContractualBillingSheet(
          invoiceKey,
          file
        )

        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: `Billing sheet uploaded successfully. ${res.data.line_items_created || 0} line items created, ${res.data.candidates_matched || 0} candidates matched.`,
              icon: 'CheckIcon',
              variant: 'success'
            }
          })
          // Reset form
          this.billingSheetForm = {
            invoice_id: null,
            file: null
          }
          this.billingSheetPreview = []
          if (this.$refs.contractualBillingTab) {
            this.$refs.contractualBillingTab.resetForm()
            this.$refs.contractualBillingTab.updatePreview([])
          }
          // Reload invoices
          this.loadInvoices()
          this.loadAllMonthsInvoices({ force: true })
          this.loadContractualInvoices()
        } else {
          throw new Error(this.billingSheetUploadError(res, invoiceKey))
        }
      } catch (error) {
        console.error('[InvoiceDashboard] Error uploading billing sheet:', error)
        const text = this.billingSheetUploadError(error, invoiceKey)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.uploadingSheet = false
      }
    },

    billingSheetUploadError(resOrErr, invoiceKey) {
      const fallback = 'Failed to upload billing sheet'
      if (!resOrErr) return fallback
      const httpStatus = resOrErr.httpStatus
        || (typeof resOrErr.status === 'number' ? resOrErr.status : null)
        || (resOrErr.response && resOrErr.response.status)
        || null
      const nested = (resOrErr.data && (resOrErr.data.message || resOrErr.data.error))
        || (resOrErr.response && resOrErr.response.data && (resOrErr.response.data.message || resOrErr.response.data.error))
        || ''
      const raw = resOrErr.message || nested || ''
      const msg = String(raw || '').trim()
      if (
        httpStatus === 404
        || /invoice not found/i.test(msg)
        || /request failed with status code 404/i.test(msg)
        || msg === '404'
      ) {
        return invoiceKey
          ? `Invoice ${invoiceKey} was not found. Confirm the invoice number and try again.`
          : 'Invoice not found. Confirm the invoice number and try again.'
      }
      if (msg && !/^request failed with status code/i.test(msg) && msg !== '404') {
        return msg
      }
      return fallback
    },

    onBillingSheetFileChange(event) {
      const file = (event && event.target && event.target.files && event.target.files[0])
        || (event && event.target && event.target.value)
        || (Array.isArray(event) ? event[0] : event)
      if (!file || typeof file === 'string') {
        this.billingSheetPreview = []
        if (this.$refs.contractualBillingTab) this.$refs.contractualBillingTab.updatePreview([])
        return
      }

      this.billingSheetForm = {
        ...this.billingSheetForm,
        file
      }

      // Simple CSV preview parsing (Excel needs a dedicated parser)
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const text = e.target.result
          const lines = text.split('\n').filter(line => line.trim())
          if (!lines.length) {
            this.billingSheetPreview = []
            if (this.$refs.contractualBillingTab) this.$refs.contractualBillingTab.updatePreview([])
            return
          }
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase())

          // Check if required columns exist
          const requiredCols = ['candidate', 'days', 'rate', 'amount']
          const hasAllCols = requiredCols.every(col =>
            headers.some(h => h.includes(col))
          )

          if (!hasAllCols) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Warning',
                text: 'File should contain columns: Candidate, Days, Rate, Amount',
                icon: 'AlertCircleIcon',
                variant: 'warning'
              }
            })
            this.billingSheetPreview = []
            if (this.$refs.contractualBillingTab) this.$refs.contractualBillingTab.updatePreview([])
            return
          }

          // Parse preview (first 5 rows)
          this.billingSheetPreview = lines.slice(1, 6).map(line => {
            const values = line.split(',')
            return {
              candidate: values[0]?.trim() || '',
              days: values[1]?.trim() || '',
              rate: values[2]?.trim() || '',
              amount: values[3]?.trim() || ''
            }
          })
          if (this.$refs.contractualBillingTab) {
            this.$refs.contractualBillingTab.updatePreview(this.billingSheetPreview)
          }
        } catch (error) {
          console.error('[InvoiceDashboard] Error parsing file preview:', error)
          this.billingSheetPreview = []
          if (this.$refs.contractualBillingTab) this.$refs.contractualBillingTab.updatePreview([])
        }
      }
      reader.readAsText(file)
    },

    // Calculate Due Date based on Payment Terms
    async calculateDueDate(invoiceDate, clientId) {
      try {
        if (!invoiceDate || !clientId) {
          return null
        }

        const res = await enhancedInvoiceService.getClientPaymentTerms(clientId)
        if (res && res.status === 'OK' && res.data && res.data.payment_terms_days) {
          const dueDate = moment(invoiceDate).add(res.data.payment_terms_days, 'days')
          return dueDate.format('YYYY-MM-DD')
        }
        
        // Default to 30 days if payment terms not found
        return moment(invoiceDate).add(30, 'days').format('YYYY-MM-DD')
      } catch (error) {
        console.error('[InvoiceDashboard] Error calculating due date:', error)
        // Default to 30 days on error
        return moment(invoiceDate).add(30, 'days').format('YYYY-MM-DD')
      }
    },

    viewRecruiterDetails(recruiterId) {
      if (recruiterId) {
        // Navigate to recruiter details page if exists
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Info',
            text: 'Recruiter details view will be implemented',
            icon: 'InfoIcon',
            variant: 'info'
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
$inv-teal: #0099AC;
$inv-teal-dark: #008B7D;
$inv-border: #e8ecf0;
$inv-muted: #64748b;
$inv-bg: #f7f9fb;

.invoice-dashboard {
  --inv-teal: #{$inv-teal};
  --inv-border: #{$inv-border};
  --inv-muted: #{$inv-muted};
  --inv-bg: #{$inv-bg};
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.invoice-dash-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0.65rem;

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
  }

  .btn {
    min-height: 2.5rem;
  }

  .btn-icon {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}

.invoice-workspace {
  border: 1px solid $inv-border !important;
  border-radius: 12px !important;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04) !important;
  overflow: hidden;
  max-width: 100%;
}

.invoice-filter-toolbar {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #fff;
  border: 1px solid $inv-border;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  padding: 0.65rem 0.85rem 0.55rem;
}

.invoice-filter-toolbar__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
}

.invoice-filter-toolbar__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.35rem;
  margin-left: auto;
}

.invoice-filters-icon-btn {
  min-height: 2.5rem;
  font-weight: 500;
  white-space: nowrap;
}

.invoice-status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  min-width: 0;
  flex: 1 1 auto;
}

.invoice-status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  border: 1px solid $inv-border;
  background: $inv-bg;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  min-height: 2.5rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: rgba($inv-teal, 0.4);
    color: $inv-teal-dark;
  }

  &.is-active {
    background: rgba($inv-teal, 0.12);
    border-color: rgba($inv-teal, 0.45);
    color: $inv-teal-dark;
  }

  &--danger.is-active {
    background: rgba(194, 59, 59, 0.1);
    border-color: rgba(194, 59, 59, 0.35);
    color: #a12f2f;
  }

  &__count {
    min-width: 1.15rem;
    padding: 0.12rem 0.35rem;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.06);
    font-size: 0.65rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: inherit;
  }

  &.is-active &__count {
    background: rgba($inv-teal, 0.16);
  }

  &--danger.is-active &__count {
    background: rgba(194, 59, 59, 0.12);
  }
}

.invoice-filter-primary {
  display: grid;
  grid-template-columns: minmax(140px, 1.4fr) repeat(2, minmax(110px, 0.8fr)) auto;
  gap: 0.5rem 0.65rem;
  align-items: end;
}

.invoice-filter-field {
  min-width: 0;

  > label {
    display: block;
    margin-bottom: 0.15rem;
    font-size: 0.68rem;
    font-weight: 650;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $inv-muted;
  }

  .form-control,
  .form-control-sm,
  .custom-select,
  .custom-select-sm {
    min-height: 2.5rem;
    font-size: 0.82rem;
  }

  .btn {
    min-height: 2.5rem;
  }
}

.invoice-filter-count {
  font-size: 0.65rem;
  vertical-align: middle;
}

.invoice-overview-filters-modal {
  .modal-title {
    font-size: 1rem;
    font-weight: 650;
    color: $inv-teal-dark;
  }

  .invoice-filter-primary {
    margin-bottom: 0;
  }
}

.invoice-filters-advanced {
  margin-top: 0.55rem;
  padding-top: 0.55rem;
  border-top: 1px dashed $inv-border;
}

.invoice-filters-row {
  margin-left: -0.4rem;
  margin-right: -0.4rem;

  > [class*='col'] {
    padding-left: 0.4rem;
    padding-right: 0.4rem;
    margin-bottom: 0.55rem;
  }

  .form-group {
    margin-bottom: 0;

    > label,
    .col-form-label {
      margin-bottom: 0.15rem;
      font-size: 0.72rem;
      font-weight: 600;
      color: $inv-muted;
    }
  }

  .form-control,
  .custom-select,
  .custom-select-sm {
    min-height: 2.5rem;
  }
}

.invoice-filter-checkbox-field {
  display: flex;
  align-items: center;
  min-height: 2.5rem;
}

.invoice-workspace__tabs {
  max-width: 100%;
  min-width: 0;

  .invoice-dash-tabs {
    margin: 0;
    padding: 0.35rem 0 0;
    border-bottom: 1px solid $inv-border;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(15, 23, 42, 0.18);
      border-radius: 999px;
    }

    .nav-item {
      flex: 0 0 auto;
    }

    .nav-link {
      white-space: nowrap;
      font-size: 0.8rem;
      font-weight: 600;
      color: $inv-muted;
      min-height: 2.5rem;
      padding: 0.6rem 0.85rem;
      border: none;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
      display: inline-flex;
      align-items: center;

      &:hover {
        color: $inv-teal-dark;
      }

      &.active {
        color: $inv-teal-dark;
        background: transparent;
        border-bottom-color: $inv-teal;
      }
    }
  }

  .invoice-tab-content {
    padding-top: 0.75rem;
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
  }
}

/* <=991 — tablet */
@media (max-width: 991.98px) {
  .invoice-filter-primary {
    grid-template-columns: 1fr 1fr;

    .invoice-filter-field--actions {
      grid-column: 1 / -1;
    }
  }

  .invoice-workspace__tabs {
    .invoice-dash-tabs .nav-link {
      font-size: 0.78rem;
      padding-left: 0.7rem;
      padding-right: 0.7rem;
    }
  }
}

/* <=767 — large phone / small tablet */
@media (max-width: 767.98px) {
  .invoice-filter-toolbar__top {
    flex-wrap: nowrap;
    align-items: center;
  }

  .invoice-status-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0.15rem;
    scrollbar-width: thin;
  }

  .invoice-filter-toolbar {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .invoice-filter-primary {
    .invoice-filter-field--actions .d-flex {
      width: 100%;
    }

    .invoice-filter-field--actions .btn {
      flex: 1 1 auto;
    }
  }
}

/* <=575 — phone */
@media (max-width: 575.98px) {
  .invoice-dash-header {
    &__actions {
      width: 100%;
      justify-content: flex-end;
    }

    &__recon .btn-label {
      display: none;
    }
  }

  .invoice-filter-primary {
    grid-template-columns: 1fr;
  }

  .invoice-filter-toolbar {
    padding-left: 0.65rem;
    padding-right: 0.65rem;
  }

  .invoice-filters-icon-btn {
    min-width: 2.5rem;
    padding-left: 0.55rem;
    padding-right: 0.55rem;
  }

  .invoice-workspace__tabs {
    .invoice-tab-content {
      padding-top: 0.6rem;
    }
  }
}
</style>

